import { Language } from '../stores/languageStore';

export interface TranslationKeys {
  common: {
    manage: string;
    settings: string;
    save: string;
    cancel: string;
    confirm: string;
    loading: string;
    noData: string;
    notAvailable: string;
    debugInfo: string;
    on: string;
    off: string;
    close: string;
    all: string;
  };
  pwaUpdate: {
    title: string;
    newVersionAvailable: string;
    updateNow: string;
    updating: string;
    later: string;
  };
  sidebar: {
    sections: { workspace: string; reference: string };
    dashboard: string;
    deviceManager: string;
    visualizer: string;
    dataCenter: string;
    streamProcess: string;
    documents: string;
    settings: string;
    deviceConnected: string;
    sdkReady: string;
    webBluetooth: string;
    closeLabel: string;
    expandLabel: string;
    collapseLabel: string;
  };
  topbar: {
    menuLabel: string;
    dashboard: string;
    noDevice: string;
  };
  statusbar: {
    connection: string;
    connected: string;
    disconnected: string;
    streaming: string;
    recording: string;
    battery: string;
    samplingRate: string;
    dataSize: string;
    elapsed: string;
    sensors: string;
    goodContact: string;
    poorContact: string;
    stopped: string;
  };
  dashboard: {
    title: string;
    subtitle: string;
    connected: string;
    waiting: string;
    linkBandStatus: string;
    linkBandStatusSub: string;
    goToLinkBand: string;
    dataVisualization: string;
    dataVisualizationSub: string;
    connectPlease: string;
    goToVisualizer: string;
    dataStorage: string;
    dataStorageSub: string;
    settings: string;
    storageReady: string;
    goToDataCenter: string;
    connection: string;
    connectionDuration: string;
    connectFromDeviceManager: string;
    recording: string;
    recordingDuration: string;
  };
  deviceManager: {
    title: string;
    subtitle: string;
    pageHeading: string;
    pageSubheading: string;
    scan: string;
    scanning: string;
    scanDevices: string;
    demoTry: string;
    demoTryHint: string;
    demoBannerTitle: string;
    demoBannerDesc: string;
    demoExit: string;
    demoStartFailed: string;
    refresh: string;
    connect: string;
    connecting: string;
    disconnect: string;
    disconnecting: string;
    forceDisconnect: string;
    connected: string;
    disconnected: string;
    noDevices: string;
    noDevicesDesc: string;
    deviceFound: string;
    deviceName: string;
    deviceId: string;
    rssi: string;
    registered: string;
    notRegistered: string;
    register: string;
    unregister: string;
    rename: string;
    storageSettings: string;
    battery: string;
    firmwareVersion: string;
    lastConnected: string;
    autoConnect: string;
    autoConnectDesc: string;
    autoRegister: string;
    autoRegisterDesc: string;
    browserNotSupported: string;
    browserNotSupportedDesc: string;
    bleNotAvailable: string;
    bleNotAvailableDesc: string;
    modalClose: string;
    confirmDisconnect: string;
    confirmDisconnectDesc: string;
    // Notification titles
    iosBrowserGuide: string;
    iosBrowserGuideMsg: string;
    bluetoothNotSupported: string;
    bluetoothNotSupportedMsg: string;
    scanComplete: string;
    scanCompleteMsg: string;
    scanFailed: string;
    scanFailedMsg: string;
    deviceConnectedTitle: string;
    deviceConnectedMsg: string;
    deviceAutoRegistered: string;
    deviceAutoRegisteredMsg: string;
    autoRegistrationFailed: string;
    autoRegistrationFailedMsg: string;
    connectionFailed: string;
    connectionFailedMsg: string;
    connectionFailedPairedHint: string;
    deviceDisconnected: string;
    deviceDisconnectedMsg: string;
    disconnectionFailed: string;
    disconnectionFailedMsg: string;
    forceDisconnectComplete: string;
    forceDisconnectCompleteMsg: string;
    forceDisconnectFailed: string;
    forceDisconnectFailedMsg: string;
    deviceRegistered: string;
    deviceRegisteredMsg: string;
    registrationFailed: string;
    registrationFailedMsg: string;
    deviceUnregistered: string;
    deviceUnregisteredMsg: string;
    unregistrationFailed: string;
    unregistrationFailedMsg: string;
    deviceRenamed: string;
    deviceRenamedMsg: string;
    renameFailed: string;
    renameFailedMsg: string;
    nicknameTooLong: string;
    systemInitFailed: string;
    systemInitFailedMsg: string;
    // UI labels
    manageDevices: string;
    availableDevices: string;
    registeredDevices: string;
    iosUserGuide: string;
    androidUserGuide: string;
    desktopUserGuide: string;
    iosSafariNotSupported: string;
    iosRecommendedBrowser: string;
    iosAlternativeBrowser: string;
    iosDownloadInstruction: string;
    devicePrepTitle: string;
    devicePrepPower: string;
    devicePrepPairing: string;
    devicePrepClose: string;
    devicePrepBrowserAndroid: string;
    devicePrepBrowserDesktop: string;
    deviceConnecting: string;
    deviceConnectingDesc: string;
    connectionFailedTitle: string;
    unknownError: string;
    solution: string;
    solutionPower: string;
    solutionPairing: string;
    solutionRefresh: string;
    realtimeReceiving: string;
    live: string;
    disconnectingShort: string;
    disconnectShort: string;
    forceDisconnectShort: string;
    chargingLabel: string;
    dischargingLabel: string;
    scanningShort: string;
    scanningForDevices: string;
    pairingHint: string;
    available: string;
    connectedStatus: string;
    autoConnectWaiting: string;
    noAvailableDevices: string;
    noAvailableDevicesDesc: string;
    scanForDevices: string;
    autoConnectShort: string;
    autoRegisterShort: string;
    noRegisteredDevices: string;
    noRegisteredDevicesDesc: string;
    connectionCount: string;
    lastShort: string;
    registeredOn: string;
    idLabel: string;
    viewDetails: string;
    storageSettingsShort: string;
    unregisterShort: string;
    registerYourDevice: string;
    registerYourDeviceDesc: string;
    registerConfirmText: string;
    deviceNickname: string;
    nicknameOptional: string;
    nicknameMaxLength: string;
    nicknamePlaceholder: string;
    nicknameHelp: string;
    nicknameEmptyHelp: string;
    skip: string;
    registerAction: string;
    deviceNicknameChange: string;
    deviceNicknameChangeDesc: string;
    renameConfirmText: string;
    cancelAction: string;
    saveAction: string;
  };
  visualizer: {
    title: string;
    subtitle: string;
    deviceRequired: string;
    deviceRequiredDesc: string;
    connectLinkBand: string;
    streaming: string;
    waiting: string;
    noSensorData: string;
    noSensorDataDesc: string;
    receivingDataDesc: string;
    startStreamingDesc: string;
    eegBrainwave: string;
    ppgHeartRate: string;
    accAcceleration: string;
    initializing: string;
    eegFilteredTitle: string;
    ppgFilteredTitle: string;
    connectDevice: string;
    wearProperly: string;
    redChannel: string;
    irChannel: string;
    deltaBand: string;
    thetaBand: string;
    alphaBand: string;
    betaBand: string;
    gammaBand: string;
    xAxisTime: string;
    xAxisFrequency: string;
    yAxisVoltage: string;
    yAxisPower: string;
    yAxisSignalQuality: string;
    ch1SeriesFP1: string;
    ch2SeriesFP2: string;
    goToVisualizer: string;
    poorContact: string;
    signalQualityMayDegrade: string;
    poorContactDetection: string;
    contactNormal: string;
    contactPoor: string;
    eegAnalysisIndex: string;
    stabilizing: string;
    eegTitle: string;
    eegDesc: string;
    ppgTitle: string;
    ppgDesc: string;
    accTitle: string;
    accDesc: string;
    ch1FilteredEEG: string;
    ch2FilteredEEG: string;
    ch1FilteredDesc: string;
    ch2FilteredDesc: string;
    ch1SQI: string;
    ch2SQI: string;
    sqiDesc: string;
    powerSpectrum: string;
    powerSpectrumDesc: string;
    bandPower: string;
    bandPowerDesc: string;
    eegIndexes: string;
    eegIndexesDesc: string;
    filteredPPGDesc: string;
    ppgSQI: string;
    ppgSQIDesc: string;
    hrvIndexes: string;
    hrvIndexesDesc: string;
    accMagnitude: string;
    accMagnitudeDesc: string;
    accIndexes: string;
    accIndexesDesc: string;
    noGraphData: string;
    connectToView: string;
    channel1: string;
    channel2: string;
    brainwave: string;
    /** 비의료기기 면책 문구 — 지표 카드 하단에 상시 표시 */
    indexDisclaimer: string;
  };
  dataCenter: {
    title: string;
    subtitle: string;
    sessions: string;
    totalData: string;
    storageType: string;
    startRecording: string;
    stopRecording: string;
    recording: string;
    noSessions: string;
    noSessionsDesc: string;
    sessionName: string;
    duration: string;
    size: string;
    date: string;
    delete: string;
    download: string;
    upload: string;
    uploadToCloud: string;
    cloudStorage: string;
    localStorage: string;
    incompleteSessions: string;
    cleanup: string;
    storageLocation: string;
    // Header
    storageSubtitle: string;
    storageSettingsBtn: string;
    // Storage banners
    selectStorageDirectory: string;
    selectStorageDirectoryDesc: string;
    selectAction: string;
    storageConnected: string;
    // Storage overview cards
    totalStorage: string;
    used: string;
    available: string;
    // Recording control
    realtimeStreamingRecording: string;
    deviceInfo: string;
    connectedLinkBand: string;
    deviceNameLabel: string;
    deviceIdLabel: string;
    connectionStatus: string;
    connected: string;
    batteryLabel: string;
    connectionTime: string;
    samplingRateLabel: string;
    storageInfoCard: string;
    storageLocationLabel: string;
    storagePath: string;
    copyPath: string;
    openFolder: string;
    connectDeviceTitle: string;
    connectDeviceDesc: string;
    connectDeviceTip: string;
    recordingControl: string;
    deviceConnected: string;
    deviceNotConnected: string;
    storageReady: string;
    storageSetupNeeded: string;
    dataStreaming: string;
    streamingStopped: string;
    fileSaving: string;
    saveWaiting: string;
    sessionNamePlaceholder: string;
    durationLabel: string;
    dataSize: string;
    savingProgress: string;
    stopRecordingShort: string;
    startRecordingShort: string;
    saveSettings: string;
    indexedDbInfo: string;
    indexedDbDesc: string;
    dataTypesToSave: string;
    // Toasts and alerts
    cannotDeleteActive: string;
    cannotDeleteActiveDesc: string;
    sessionDeleted: string;
    androidDeleteLimited: string;
    sessionDeleteFailed: string;
    androidAccessLimited: string;
    cannotDownloadActive: string;
    cannotDownloadActiveDesc: string;
    sessionDownloadFailed: string;
    cannotCopyPathActive: string;
    cannotCopyPathActiveDesc: string;
    pathCopied: string;
    pathCopiedDesc: string;
    pathCopyFailed: string;
    pathCopyFailedDesc: string;
    storagePathCopied: string;
    storagePathCopiedDesc: string;
    folderOpenFailed: string;
    folderOpenFailedDesc: string;
    storageFolderPathCopied: string;
    stopRecordingFailedAlert: string;
    connectDeviceFirstAlert: string;
    selectStorageFirstAlert: string;
    awsCredentialsAlert: string;
    startRecordingFailedAlert: string;
    storageRestoreError: string;
    storageRestoreErrorDesc: string;
    // Search / Filter
    allData: string;
    eegOnly: string;
    ppgOnly: string;
    accOnly: string;
    today: string;
    thisWeek: string;
    searchPlaceholder: string;
    advancedFilter: string;
    // Table
    savedSessions: string;
    newest: string;
    oldest: string;
    colSessionName: string;
    colDateTime: string;
    colDuration: string;
    colDevice: string;
    colDataTypes: string;
    colSize: string;
    colLocation: string;
    colActions: string;
    uploading: string;
    recordingInProgress: string;
    copyAbsolutePath: string;
    downloadSession: string;
    deleteSession: string;
    noSessionsFound: string;
    noSessionsFoundDesc: string;
    loadingSessions: string;
    // Quality badges
    qualityExcellent: string;
    qualityGood: string;
    qualityFair: string;
    qualityPoor: string;
    // Folder instructions
    folderWinTitle: string;
    folderWinDesc: string;
    folderMacTitle: string;
    folderMacDesc: string;
    folderLinuxTitle: string;
    folderLinuxDesc: string;
    folderGenericTitle: string;
    folderGenericDesc: string;
  };
  streamProcess: {
    title: string;
    subtitle: string;
    broadcastServer: string;
    serverUrl: string;
    connect: string;
    disconnect: string;
    connected: string;
    notConnected: string;
    healthCheck: string;
    deviceStatus: string;
    deviceName: string;
    deviceId: string;
    connectionStatus: string;
    battery: string;
    noDeviceAlert: string;
    mode: string;
    mockMode: string;
    broadcastId: string;
    mockHint: string;
    streamingControl: string;
    sent: string;
    received: string;
    subscribers: string;
    avgResponse: string;
    startSubscription: string;
    stopSubscription: string;
    viewStats: string;
    mockSimulation: string;
    mockSimulationDesc: string;
    eegRawLabel: string;
    ppgRawLabel: string;
    accRawLabel: string;
    analysisMetricsLabel: string;
    combinedDataLabel: string;
    continuousStreamLabel: string;
    stopSimulationLabel: string;
    serverStats: string;
    messagesLabel: string;
    messages: string;
    seconds: string;
    noMessages: string;
    noMessagesDesc: string;
    clear: string;
    close: string;
    developerApi: string;
    developerApiDesc: string;
    sseSubscribe: string;
    copy: string;
    messageStructure: string;
    optionalFieldsNote: string;
    apiEndpoints: string;
    subscribeEndpointDesc: string;
    publishEndpointDesc: string;
    healthEndpointDesc: string;
    statsEndpointDesc: string;
    quickTest: string;
    quickTestTerminal: string;
    quickTestDesc: string;
    pythonSSEClient: string;
    curlTest: string;
    serverHealthCheck: string;
    serverStatsQuery: string;
    curlBufferNote: string;
    status: string;
    uptime: string;
    activeDevice: string;
    totalConnections: string;
    libRequired: string;
    connectedToast: string;
    connectedMockDesc: string;
    connectFailed: string;
    unknownError: string;
    disconnectedToast: string;
    healthOk: string;
    healthFail: string;
    statsOk: string;
    statsFail: string;
    connectFirst: string;
    dataSent: string;
    dataSentDesc: string;
    dataSendFail: string;
    simulationComplete: string;
    simulationStarted: string;
    simulationStopped: string;
    subscriptionStarted: string;
    subscriptionStopped: string;
    copyJSToast: string;
    copyPythonToast: string;
    copyCurlToast: string;
    copyCmdToast: string;
  };
  settings: {
    title: string;
    subtitle: string;
    deviceManager: string;
    deviceManagerDesc: string;
    autoConnect: string;
    autoConnectDesc: string;
    autoRegister: string;
    autoRegisterDesc: string;
    deviceManagerInfo: string;
    autoConnectInfo: string;
    autoRegisterInfo: string;
    manualModeInfo: string;
    noDuplicateInfo: string;
    timeZone: string;
    timeZoneDesc: string;
    timeZoneLabel: string;
    timeZoneSystem: string;
    timeZoneKorea: string;
    timeZoneUtc: string;
    timeZoneNote: string;
    timeZoneInfo: string;
    timeZoneSystemInfo: string;
    timeZoneKoreaInfo: string;
    timeZoneUtcInfo: string;
    timeZoneApplyInfo: string;
    visualizer: string;
    visualizerDesc: string;
    visualizerComingSoon: string;
    language: string;
    languageDesc: string;
    languageKo: string;
    languageEn: string;
  };
  documents: {
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    browseDocs: string;
    browseDocsDesc: string;
    viewDocs: string;
    contents: string;
    docs: string;
    previous: string;
    next: string;
    selectDoc: string;
    selectDocDesc: string;
    sdkDocumentation: string;
    sdkHeroDesc: string;
    menuLabel: string;
    sectionDesc: {
      overview: string;
      gettingStarted: string;
      bleProtocol: string;
      apiReference: string;
      dataGuide: string;
      interface: string;
    };
  };
  exportModal: {
    title: string;
    fileSize: string;
    formatSelection: string;
    recommended: string;
    cancel: string;
    selectLocation: string;
    saveMethodTitle: string;
    saveToDevice: string;
    saveToDeviceDesc: string;
    shareToApps: string;
    shareToAppsDesc: string;
    saveLocalSuccessDesc: string;
    locationSelecting: string;
    locationSelectingDesc: string;
    progressTitle: string;
    progressZip: string;
    progressAll: string;
    progressFormat: (format: string) => string;
    progressLabel: string;
    successTitle: string;
    successDesc: string;
    done: string;
    errorTitle: string;
    retry: string;
    unknownError: string;
    fmtZipName: string;
    fmtZipDesc: string;
    fmtCsvName: string;
    fmtCsvDesc: string;
    fmtJsonName: string;
    fmtJsonDesc: string;
    fmtJsonlName: string;
    fmtJsonlDesc: string;
    fmtBinaryName: string;
    fmtBinaryDesc: string;
    fmtAllName: string;
    fmtAllDesc: string;
    sizeCompressed50: string;
    sizeOriginal70: string;
    sizeOriginal65: string;
    sizeCompressed30: string;
  };
  sessionManager: {
    recordingControl: string;
    sessionNameOptional: string;
    startRecording: string;
    stopRecording: string;
    recordingPrefix: string;
    duration: string;
    dataLabel: string;
    liveBadge: string;
    connectDeviceFirst: string;
    deviceNotConnectedAlert: string;
    currentSession: string;
    inProgress: string;
    sessionName: string;
    startTime: string;
    device: string;
    dataCollection: string;
    exportAllZip: string;
    exportEeg: string;
    exportPpg: string;
    exportAcc: string;
    exportSystemLog: string;
    storageInfo: string;
    storedSessions: string;
    used: string;
    available: string;
    usageRate: string;
    refresh: string;
    noSavedSessions: string;
    savedSessionsCount: (count: number) => string;
    colSession: string;
    colDevice: string;
    colType: string;
    colDate: string;
    colDuration: string;
    colSamples: string;
    colSize: string;
    colStatus: string;
    colActions: string;
    statusCompleted: string;
    statusProcessing: string;
    exportDialogTitle: string;
    exportDialogSessionLabel: string;
    exportDialogSelect: string;
    zipDescription: string;
    eegDataCount: (n: number) => string;
    ppgDataCount: (n: number) => string;
    accDataCount: (n: number) => string;
    systemLogLabel: string;
    previous: string;
    next: string;
    sessionRangeOf: (start: number, end: number, total: number) => string;
    confirmDelete: string;
    csvNotFoundAlert: string;
    exportFailedAlert: string;
    exportButton: string;
  };
  brainStateIndicator: {
    title: string;
    deviceRequired: string;
    sensorContactRequired: string;
    instruction: string;
    movingAverage: string;
    footerNote: string;
    focus: string;
    relaxation: string;
    stress: string;
    attention: string;
    meditation: string;
    cognitiveLoad: string;
    levelLow: string;
    levelMedium: string;
    levelHigh: string;
  };
  performanceMonitor: {
    title: string;
    notConnected: string;
    statusGood: string;
    statusWarning: string;
    statusCritical: string;
    statusUnknown: string;
    processingTime: string;
    memoryUsage: string;
    bufferSize: string;
    droppedPackets: string;
    timestampSync: string;
    timestampDrift: string;
    packetsUnit: string;
  };
  browserCompatibility: {
    title: string;
    currentBrowser: (browser: string, platform: string) => string;
    bluetoothAPILabel: string;
    supported: string;
    unsupported: string;
    secure: string;
    insecure: string;
    securityContext: string;
    unsupportedAlert: string;
    iOSUserGuide: string;
    iOSSafariDesc: string;
    capacitorNativeTitle: string;
    capacitorNativeDesc: string;
    bluefyTitle: string;
    bluefyDesc: string;
    downloadBluefy: string;
    webBLETitle: string;
    webBLEDesc: string;
    downloadWebBLE: string;
    howToUse: string;
    step1: string;
    step2: string;
    step3: string;
    androidUserGuide: string;
    androidSupportedMsg: string;
    androidChromeMsg: string;
    androidReadyTitle: string;
    androidReadyDesc: string;
    androidBrowserTitle: string;
    androidBrowserDesc: string;
    downloadChrome: string;
    desktopUserGuide: string;
    desktopSupportedMsg: string;
    desktopBrowserMsg: string;
    desktopReadyTitle: string;
    desktopReadyDesc: string;
    desktopBrowserTitle: string;
    supportedBrowsers: string;
    showDetails: string;
    hideDetails: string;
    technicalDetails: string;
  };
  mobileStorageGuide: {
    title: string;
    description: string;
    autoDownloadTitle: string;
    autoDownloadDesc: string;
    currentSession: string;
    eegData: (n: number) => string;
    ppgData: (n: number) => string;
    accData: (n: number) => string;
    howItWorks: string;
    autoSave30s: string;
    autoSave50MB: string;
    finalSave: string;
    checkDownloads: string;
    savedFiles: string;
    openDownloads: string;
    confirm: string;
    note: string;
    noteTimestamp: string;
    noteTransfer: string;
    manualCheck: string;
  };
}

// 한국어 번역
const ko: TranslationKeys = {
  common: {
    manage: '관리',
    settings: '설정',
    save: '저장',
    cancel: '취소',
    confirm: '확인',
    loading: '로딩 중...',
    noData: '데이터 없음',
    notAvailable: '—',
    debugInfo: '디버그 정보',
    on: '켜짐',
    off: '꺼짐',
    close: '닫기',
  },
  pwaUpdate: {
    title: '새 버전 업데이트',
    newVersionAvailable: 'LINK BAND SDK의 새 버전이 사용 가능합니다',
    updateNow: '지금 업데이트',
    updating: '업데이트 중...',
    later: '나중에',
  },
  sidebar: {
    sections: { workspace: 'Workspace', reference: 'Reference' },
    dashboard: 'Dashboard',
    deviceManager: 'Device Manager',
    visualizer: 'Visualizer',
    dataCenter: 'Data Center',
    streamProcess: 'Stream Process',
    documents: 'Documents',
    settings: 'Settings',
    deviceConnected: 'Device connected',
    sdkReady: 'SDK runtime ready',
    webBluetooth: 'web bluetooth · ble',
    closeLabel: '사이드바 닫기',
    expandLabel: '사이드바 펼치기',
    collapseLabel: '사이드바 접기',
  },
  topbar: {
    menuLabel: '메뉴 열기',
    dashboard: 'Dashboard',
    noDevice: '장치 없음',
  },
  statusbar: {
    connection: '연결',
    connected: '연결됨',
    disconnected: '연결 해제됨',
    streaming: '스트리밍',
    recording: '녹화',
    battery: '배터리',
    samplingRate: '샘플링 레이트',
    dataSize: '데이터 크기',
    elapsed: '경과 시간',
    sensors: '센서',
    goodContact: '양호',
    poorContact: '접촉 불량',
    stopped: '중지됨',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'LINK BAND SDK 대시보드',
    connected: 'LINK BAND 연결됨',
    waiting: '연결 대기 중',
    linkBandStatus: 'LINK BAND 상태',
    linkBandStatusSub: '연결 · 배터리 · 샘플링 레이트',
    goToLinkBand: 'LINK BAND로 이동',
    dataVisualization: '데이터 시각화',
    dataVisualizationSub: '실시간 EEG · PPG 미리보기',
    connectPlease: '디바이스를 연결해주세요',
    goToVisualizer: 'Visualizer로 이동',
    dataStorage: '데이터 저장소',
    dataStorageSub: 'IndexedDB · 브라우저 내장',
    settings: '설정',
    storageReady: '저장소 준비 완료',
    goToDataCenter: 'Data Center로 이동',
    connection: 'Connection',
    connectionDuration: 'Duration:',
    connectFromDeviceManager: 'Device Manager에서 연결',
    recording: 'Recording',
    recordingDuration: '레코딩 시간:',
  },
  deviceManager: {
    title: 'Device Manager',
    subtitle: 'BLE 디바이스 검색 및 연결 관리',
    pageHeading: 'Device Manager',
    pageSubheading: 'LINK BAND 디바이스를 관리하세요',
    scan: '스캔 시작',
    scanning: '스캔 중...',
    scanDevices: '디바이스 스캔',
    demoTry: '데모 체험',
    demoTryHint: '기기가 없어도 가상 LINK BAND 로 앱을 체험할 수 있어요.',
    demoBannerTitle: '데모 모드',
    demoBannerDesc: '실제 기기가 아닌 시뮬레이션 데이터입니다. 측정·진단에 사용할 수 없습니다.',
    demoExit: '데모 종료',
    demoStartFailed: '데모 모드를 시작하지 못했습니다.',
    refresh: '새로고침',
    connect: '연결',
    connecting: '연결 중...',
    disconnect: '연결 해제',
    disconnecting: '해제 중...',
    forceDisconnect: '강제 연결 해제',
    connected: '연결됨',
    disconnected: '연결 해제됨',
    noDevices: '검색된 디바이스 없음',
    noDevicesDesc: 'BLE 디바이스를 검색하려면 스캔을 시작하세요.',
    deviceFound: '개의 디바이스를 찾았습니다',
    deviceName: '디바이스 이름',
    deviceId: '디바이스 ID',
    rssi: '신호 세기',
    registered: '등록됨',
    notRegistered: '미등록',
    register: '등록',
    unregister: '등록 해제',
    rename: '이름 변경',
    storageSettings: '저장소 설정',
    battery: '배터리',
    firmwareVersion: '펌웨어 버전',
    lastConnected: '마지막 연결',
    autoConnect: '자동 연결',
    autoConnectDesc: '스캔된 디바이스를 자동으로 연결합니다.',
    autoRegister: '자동 등록',
    autoRegisterDesc: '연결된 디바이스를 자동으로 등록합니다.',
    browserNotSupported: '브라우저가 Web Bluetooth를 지원하지 않습니다',
    browserNotSupportedDesc: 'Chrome, Edge 등 최신 브라우저를 사용해주세요.',
    bleNotAvailable: 'Bluetooth를 사용할 수 없습니다',
    bleNotAvailableDesc: '기기의 Bluetooth가 켜져 있는지 확인해주세요.',
    modalClose: '닫기',
    confirmDisconnect: '연결 해제 확인',
    confirmDisconnectDesc: '정말로 연결을 해제하시겠습니까?',
    iosBrowserGuide: 'iOS 브라우저 안내',
    iosBrowserGuideMsg: 'iOS에서는 Bluefy 또는 WebBLE 브라우저를 사용해주세요. App Store에서 다운로드 가능합니다.',
    bluetoothNotSupported: 'Bluetooth 미지원',
    bluetoothNotSupportedMsg: 'Chrome 또는 Edge 브라우저를 사용해주세요.',
    scanComplete: '스캔 완료',
    scanCompleteMsg: '개의 디바이스를 찾았습니다',
    scanFailed: '스캔 실패',
    scanFailedMsg: '디바이스 스캔에 실패했습니다',
    deviceConnectedTitle: '디바이스 연결됨',
    deviceConnectedMsg: '디바이스에 성공적으로 연결되었습니다',
    deviceAutoRegistered: '디바이스 자동 등록됨',
    deviceAutoRegisteredMsg: '이(가) 자동으로 등록되었습니다.',
    autoRegistrationFailed: '자동 등록 실패',
    autoRegistrationFailedMsg: '디바이스 자동 등록에 실패했습니다',
    connectionFailed: '연결 실패',
    connectionFailedMsg: '디바이스 연결에 실패했습니다',
    connectionFailedPairedHint: 'macOS/Windows 블루투스 설정에 LINK BAND가 페어링되어 있다면 "기기 삭제" 후 밴드 전원을 껐다 켜고 다시 시도하세요.',
    deviceDisconnected: '디바이스 연결 해제됨',
    deviceDisconnectedMsg: '디바이스 연결이 해제되었습니다',
    disconnectionFailed: '연결 해제 실패',
    disconnectionFailedMsg: '디바이스 연결 해제에 실패했습니다',
    forceDisconnectComplete: '강제 연결 해제 완료',
    forceDisconnectCompleteMsg: '디바이스 연결이 강제로 해제되었습니다. 재연결이 가능합니다.',
    forceDisconnectFailed: '강제 연결 해제 실패',
    forceDisconnectFailedMsg: '강제 연결 해제에 실패했습니다. 브라우저를 새로고침해주세요.',
    deviceRegistered: '디바이스 등록됨',
    deviceRegisteredMsg: '이(가) 성공적으로 등록되었습니다',
    registrationFailed: '등록 실패',
    registrationFailedMsg: '디바이스 등록에 실패했습니다',
    deviceUnregistered: '디바이스 등록 해제됨',
    deviceUnregisteredMsg: '디바이스가 등록 목록에서 제거되었습니다',
    unregistrationFailed: '등록 해제 실패',
    unregistrationFailedMsg: '디바이스 등록 해제에 실패했습니다',
    deviceRenamed: '디바이스 이름 변경됨',
    deviceRenamedMsg: '디바이스 별명이 변경되었습니다.',
    renameFailed: '이름 변경 실패',
    renameFailedMsg: '디바이스 이름 변경에 실패했습니다',
    nicknameTooLong: '디바이스 별명은 20자를 초과할 수 없습니다.',
    systemInitFailed: '시스템 초기화 실패',
    systemInitFailedMsg: '시스템 초기화에 실패했습니다',
    manageDevices: 'LINK BAND 디바이스를 관리하세요',
    availableDevices: '검색된 디바이스',
    registeredDevices: '등록된 디바이스',
    iosUserGuide: '🍎 iOS 사용자 안내',
    androidUserGuide: '🤖 Android 사용자 안내',
    desktopUserGuide: '💻 데스크톱 사용자 안내',
    iosSafariNotSupported: 'iOS Safari는 Web Bluetooth를 지원하지 않습니다. 다음 브라우저를 사용해주세요:',
    iosRecommendedBrowser: '추천 브라우저 (App Store에서 다운로드)',
    iosAlternativeBrowser: '대안 브라우저 (App Store에서 다운로드)',
    iosDownloadInstruction: '앱 다운로드 후 다음 주소에 접속하세요:',
    devicePrepTitle: 'LINK BAND 디바이스 연결 준비:',
    devicePrepPower: 'LINK BAND 디바이스의 전원을 켜주세요',
    devicePrepPairing: '페어링 모드로 설정해주세요',
    devicePrepClose: '디바이스를 가까이에 두세요',
    devicePrepBrowserAndroid: 'Chrome 또는 Edge 브라우저를 사용해주세요',
    devicePrepBrowserDesktop: 'Chrome, Edge, 또는 Opera 브라우저를 사용해주세요',
    deviceConnecting: '디바이스 연결 중...',
    deviceConnectingDesc: 'LINK BAND 디바이스와 연결을 시도하고 있습니다. 잠시만 기다려주세요.',
    connectionFailedTitle: '연결 실패',
    unknownError: '알 수 없는 오류가 발생했습니다',
    solution: '해결 방법:',
    solutionPower: '디바이스 전원을 다시 켜보세요',
    solutionPairing: '페어링 모드를 다시 활성화하세요',
    solutionRefresh: '브라우저를 새로고침하고 다시 시도하세요',
    realtimeReceiving: '실시간 EEG 데이터 수신 중',
    live: 'Live',
    disconnectingShort: '연결 해제 중...',
    disconnectShort: '연결 해제',
    forceDisconnectShort: '강제 연결 해제',
    chargingLabel: '⚡ 충전 중',
    dischargingLabel: '🔋 사용 중',
    scanningShort: 'Scanning...',
    scanningForDevices: '디바이스 검색 중...',
    pairingHint: 'LINK BAND가 페어링 모드인지 확인해주세요',
    available: '사용 가능',
    connectedStatus: '연결됨',
    autoConnectWaiting: '자동 연결 대기',
    noAvailableDevices: '연결 가능한 디바이스가 없습니다',
    noAvailableDevicesDesc: '브라우저 페어링 창에서 LINK BAND를 선택하면 자동으로 연결됩니다',
    scanForDevices: '디바이스 스캔',
    autoConnectShort: '자동 연결',
    autoRegisterShort: '자동 등록',
    noRegisteredDevices: '등록된 디바이스가 없습니다',
    noRegisteredDevicesDesc: '디바이스를 스캔하여 연결하면 자동으로 등록됩니다',
    connectionCount: '연결',
    lastShort: '마지막',
    registeredOn: '등록일',
    idLabel: 'ID',
    viewDetails: '상세 보기',
    storageSettingsShort: '저장소 설정',
    unregisterShort: '등록 해제',
    registerYourDevice: '디바이스를 등록해보세요',
    registerYourDeviceDesc: '다음 연결시 더 편리한 연결이 제공됩니다',
    registerConfirmText: '을(를) 등록하시겠습니까?',
    deviceNickname: '디바이스 별명',
    nicknameOptional: '디바이스 별명 (선택사항)',
    nicknameMaxLength: '디바이스 별명 (최대 20자)',
    nicknamePlaceholder: '디바이스 별명을 입력하세요',
    nicknameHelp: '별명을 설정하면 디바이스 목록에서 쉽게 구분할 수 있습니다',
    nicknameEmptyHelp: '빈 칸으로 두면 기본 이름이 사용됩니다',
    skip: '건너뛰기',
    registerAction: '등록하기',
    deviceNicknameChange: '디바이스 별명 변경',
    deviceNicknameChangeDesc: '디바이스를 쉽게 구분할 수 있도록 별명을 설정하세요',
    renameConfirmText: '의 별명을 변경하시겠습니까?',
    cancelAction: '취소',
    saveAction: '수정',
  },
  visualizer: {
    title: 'Visualizer',
    subtitle: '실시간 데이터 시각화',
    deviceRequired: '디바이스 연결 필요',
    deviceRequiredDesc: '데이터 시각화를 위해 LINK BAND 디바이스를 연결해주세요.',
    connectLinkBand: 'Link Band 연결하기',
    streaming: '스트리밍 중',
    waiting: '대기 중',
    noSensorData: '센서 데이터가 없습니다',
    noSensorDataDesc: '스트리밍을 시작하면 실시간 분석 결과가 표시됩니다.',
    receivingDataDesc: '데이터를 수신하는 중입니다...',
    startStreamingDesc: '스트리밍을 시작하면 실시간 분석 결과가 표시됩니다.',
    eegBrainwave: 'EEG 뇌파',
    ppgHeartRate: 'PPG 심박',
    accAcceleration: 'ACC 가속도',
    initializing: '시스템 초기화 중...',
    eegFilteredTitle: '실시간 필터링된 EEG 신호',
    ppgFilteredTitle: '실시간 필터링된 PPG 신호',
    connectDevice: '디바이스를 연결해주세요',
    wearProperly: '올바르게 디바이스를 착용해주세요',
    redChannel: 'Red 채널',
    irChannel: 'IR 채널',
    deltaBand: '델타파',
    thetaBand: '세타파',
    alphaBand: '알파파',
    betaBand: '베타파',
    gammaBand: '감마파',
    xAxisTime: '시간 (초)',
    xAxisFrequency: '주파수 (Hz)',
    yAxisVoltage: '전압 (μV)',
    yAxisPower: '파워 (dB)',
    yAxisSignalQuality: '신호 품질 (%)',
    ch1SeriesFP1: '채널 1 (FP1)',
    ch2SeriesFP2: '채널 2 (FP2)',
    goToVisualizer: 'Visualizer로 이동',
    poorContact: '전극 접촉 불량',
    signalQualityMayDegrade: '신호 품질 분석 정확도가 저하될 수 있습니다',
    poorContactDetection: '전극 접촉 불량 감지',
    contactNormal: '정상',
    contactPoor: '접촉 불량',
    eegAnalysisIndex: '뇌파 분석 지수',
    stabilizing: '안정화',
    eegTitle: 'EEG 뇌파 분석',
    eegDesc: '실시간 뇌파 신호 처리 및 분석 결과를 시각화합니다.',
    ppgTitle: 'PPG 심박 분석',
    ppgDesc: '실시간 광혈류측정(PPG) 신호 처리 및 심박변이도 분석 결과를 시각화합니다.',
    accTitle: 'ACC 가속도 분석',
    accDesc: '실시간 3축 가속도계 신호 처리 및 움직임 패턴 분석 결과를 시각화합니다.',
    ch1FilteredEEG: 'Ch1 필터링된 EEG 신호 (FP1)',
    ch2FilteredEEG: 'Ch2 필터링된 EEG 신호 (FP2)',
    ch1FilteredDesc: '채널 1 (FP1) - 60Hz 노치 + 1-45Hz 밴드패스 필터 적용',
    ch2FilteredDesc: '채널 2 (FP2) - 60Hz 노치 + 1-45Hz 밴드패스 필터 적용',
    ch1SQI: 'Ch1 신호 품질 지수 (SQI)',
    ch2SQI: 'Ch2 신호 품질 지수 (SQI)',
    sqiDesc: '전극 접촉 상태와 신호 품질 모니터링',
    powerSpectrum: '파워 스펙트럼 (1-45Hz)',
    powerSpectrumDesc: 'Ch1, Ch2 주파수 도메인 뇌파 신호 분석 결과',
    bandPower: '주파수 밴드 파워',
    bandPowerDesc: '델타, 세타, 알파, 베타, 감마파 실시간 분석',
    eegIndexes: 'EEG 분석 지수',
    eegIndexesDesc: '집중력, 이완도, 스트레스 등 9개 지수 실시간 분석',
    filteredPPG: '필터링된 PPG 신호',
    filteredPPGDesc: 'Red/IR LED 신호 0.5-5.0Hz 밴드패스 필터 적용 (DC 제거)',
    ppgSQI: 'PPG 신호 품질 지수 (SQI)',
    ppgSQIDesc: '신호 품질과 전극 접촉 상태 모니터링',
    hrvIndexes: '심박변이도 분석 지수',
    hrvIndexesDesc: '심박수, HRV, 스트레스 등 6개 지수 실시간 분석',
    accMagnitude: '가속도 크기 (Magnitude)',
    accMagnitudeDesc: 'X, Y, Z축 가속도 크기 실시간 모니터링',
    accIndexes: '종합 분석 지수',
    accIndexesDesc: '활동량, 자세 변화, 움직임 패턴 종합 분석',
    noGraphData: '그래프에 표시할 데이터가 없습니다',
    connectToView: '연결 후 {channel} 신호를 실시간으로 확인할 수 있습니다',
    channel1: '채널 1',
    channel2: '채널 2',
    brainwave: '뇌파',
    // -- EEG Indexes --
    eegFocusLabel: '집중력 (Focus)',
    eegArousalLabel: '이완및긴장도 (Arousal)',
    eegStressLabel: '스트레스 (Stress)',
    eegBalanceLabel: '좌우뇌 균형',
    eegCognitiveLoadLabel: '인지 부하',
    eegValenceLabel: '정서안정성 (Valence)',
    eegTotalPowerLabel: '신경활동성',
    eegTotalPowerDesc: '전체 뇌파 활동 강도',
    eegCognitiveLoadDesc: '세타/알파 비율 기반 인지 부하',
    eegBalanceDesc: '반구간 활동 균형 지표',
    eegStressDesc: '고주파 활동 기반 스트레스',
    eegIndexNoData: '데이터 없음',
    eegSampleAvg: '개 샘플 평균',
    eegConnectToView: '연결 후 뇌파 분석 지수를 실시간으로 확인할 수 있습니다',
    eegAnalyzing: 'processedDataStore EEG 분석 결과 기반 지수 계산 중...',
    eegAccuracyDegrade: '뇌파 지수 정확도가 저하될 수 있습니다',
    eegDeviceRequired: '(디바이스 연결이 필요합니다.)',
    // EEG Status Messages
    eegFocusLowMsg: '주의력 결핍 혹은 졸음',
    eegFocusHighMsg: '과도한 집중 혹은 스트레스',
    eegFocusNormalMsg: '정상 범위',
    eegArousalLowMsg: '긴장 및 스트레스 상태',
    eegArousalHighMsg: '과도한 이완',
    eegArousalNormalMsg: '정상적인 긴장 상태',
    eegStressVeryLowMsg: '매우 낮은 스트레스',
    eegStressLowMsg: '낮은 스트레스',
    eegStressNormalMsg: '정상 범위',
    eegStressHighMsg: '높은 스트레스',
    eegStressVeryHighMsg: '매우 높은 스트레스 수치',
    eegBalanceMeasuringMsg: '측정 중',
    eegBalanceRightMsg: '창의적 (우뇌 우세)',
    eegBalanceLeftMsg: '논리적 (좌뇌 우세)',
    eegBalanceNormalMsg: '균형 상태',
    eegCognitiveLowMsg: '낮은 참여도',
    eegCognitiveOverloadMsg: '과부하 상태',
    eegCognitiveHighMsg: '높은 인지 부하',
    eegCognitiveNormalMsg: '최적 부하',
    eegValenceLowMsg: '정서 불안정, 과도한 각성',
    eegValenceHighMsg: '정서 둔화, 과도한 억제',
    eegValenceNormalMsg: '정상 범위',
    eegPowerHighMsg: '과도한 신경 활동',
    eegPowerLowMsg: '억제된 신경 활동',
    eegPowerNormalMsg: '정상 범위',
    // -- PPG Indexes --
    ppgHrvTitle: '심박변이도 분석 지수',
    ppgSignalQualityNeed: '신호 품질 개선 필요',
    ppgStabilizing: '안정화 중',
    ppgStabilized: '안정화됨',
    ppgConnectToView: '연결 후 심박변이도 분석 지수를 실시간으로 확인할 수 있습니다',
    ppgAnalyzing: 'processedDataStore PPG 분석 결과 기반 지수 계산 중...',
    ppgBpmDesc: '심박수',
    ppgSpo2Desc: '혈중 산소 포화도',
    ppgHrMaxDesc: '측정 기간 최고 심박수 (2분간)',
    ppgHrMinDesc: '측정 기간 최저 심박수 (2분간)',
    ppgStressIdxDesc: '스트레스 지수',
    ppgRmssdDesc: '연속 RR간격 차이의 제곱근',
    ppgSdnnDesc: 'RR간격의 표준편차',
    ppgSdsdDesc: '연속 차이의 표준편차',
    ppgLfDesc: '저주파 성분 (교감신경 활동)',
    ppgHfDesc: '고주파 성분 (부교감신경 활동)',
    ppgLfhfDesc: '교감/부교감 균형 비율',
    ppgAvnnDesc: '평균 NN간격 (심박주기)',
    ppgPnn50IdxDesc: '50ms 이상 차이나는 RR간격 비율',
    ppgPnn20IdxDesc: '20ms 이상 차이나는 RR간격 비율',
    // PPG Status Messages
    ppgBpmLowMsg: '낮은 심박수',
    ppgBpmHighMsg: '높은 심박수',
    ppgBpmNormalMsg: '정상 범위',
    ppgSpo2UnmeasurableMsg: '측정 불가 (신호 부족)',
    ppgSpo2SevereMsg: '산소포화도 낮음 (측정값)',
    ppgSpo2MildMsg: '산소포화도 다소 낮음',
    ppgSpo2LowNormalMsg: '정상 범위 (하한)',
    ppgSpo2NormalMsg: '정상 산소포화도',
    ppgRmssdLowMsg: '긴장 상태 (휴식이 필요한 상태)',
    ppgRmssdHighMsg: '심박 변동 큼',
    ppgRmssdNormalMsg: '정상 범위',
    ppgSdnnLowMsg: '심박 리듬 일정함 (스트레스나 피로 상태)',
    ppgSdnnHighMsg: '심박 리듬 다양함',
    ppgSdnnNormalMsg: '정상 범위',
    ppgPnn50LowMsg: '심박 리듬 규칙적 (긴장이나 피로 상태)',
    ppgPnn50HighMsg: '심박 리듬 유연함',
    ppgPnn50NormalMsg: '정상 범위',
    ppgStressVeryLowMsg: '매우 낮은 스트레스 (과도한 이완)',
    ppgStressLowMsg: '낮은 스트레스 (이완 상태)',
    ppgStressNormalMsg: '정상 범위 (균형 상태)',
    ppgStressHighMsg: '높은 스트레스 (긴장 상태)',
    ppgStressVeryHighMsg: '매우 높은 스트레스 수치',
    ppgAvnnHighMsg: '빠른 심박 (활동적이거나 긴장 상태)',
    ppgAvnnLowMsg: '느린 심박 (휴식 상태나 운동선수형)',
    ppgAvnnNormalMsg: '안정적인 심박 리듬',
    ppgLfLowMsg: '낮은 교감신경 활동 (과도한 휴식)',
    ppgLfHighMsg: '높은 교감신경 활동 (스트레스나 긴장)',
    ppgLfNormalMsg: '적절한 교감신경 활동',
    ppgHfLowMsg: '낮은 부교감신경 활동 (스트레스나 피로)',
    ppgHfHighMsg: '높은 부교감신경 활동 (깊은 휴식)',
    ppgHfNormalMsg: '적절한 부교감신경 활동',
    ppgLfhfLowMsg: '부교감신경 우세 (깊은 휴식 상태)',
    ppgLfhfHighMsg: '교감신경 우세 (스트레스나 활동)',
    ppgLfhfNormalMsg: '자율신경 균형 상태',
    ppgPnn20LowMsg: '심박 리듬 일정함 (긴장이나 피로)',
    ppgPnn20HighMsg: '심박 리듬 유연함',
    ppgPnn20NormalMsg: '적절한 심박 변화',
    ppgSdsdLowMsg: '심박 변화 적음 (스트레스나 피로)',
    ppgSdsdHighMsg: '심박 변화 활발함',
    ppgSdsdNormalMsg: '정상적인 심박 변화',
    ppgHrMaxLowMsg: '낮은 최대 심박수',
    ppgHrMaxHighMsg: '높은 최대 심박수',
    ppgHrMaxNormalMsg: '정상 최대 심박수',
    ppgHrMinLowMsg: '낮은 최소 심박수',
    ppgHrMinHighMsg: '높은 최소 심박수',
    ppgHrMinNormalMsg: '정상 최소 심박수',
    // -- ACC Indexes --
    accStabilityLabel: '안정성',
    accIntensityLabel: '강도',
    accAvgMovementLabel: 'Average Movement',
    accMaxMovementLabel: 'Max Movement',
    accStabilityDesc: '자세 안정성 지수',
    accIntensityDesc: '움직임 강도 수준',
    accAvgMovementDesc: '평균 움직임 크기',
    accMaxMovementDesc: '최대 움직임 크기',
    accAnalyzing: '상태 분석 중...',
    accSampleAnalysis: '개 샘플 분석',
    accDeviceRequired: '디바이스 연결이 필요합니다',
    accActivityTitle: '현재 활동 상태',
    accTotalSamples: '전체 샘플',
    accRecentActivity: '최근 활동도',
    accTotalActivity: '전체 활동도',
    accIndexRange: '인덱스 범위',
    accAccuracyDegrade: 'ACC 분석 정확도가 저하될 수 있습니다',
    // ACC Status Messages
    accStabilityVeryLowMsg: '매우 불안정한 자세',
    accStabilityLowMsg: '불안정한 자세',
    accStabilityNormalMsg: '정상 범위 안정성',
    accStabilityHighMsg: '매우 안정적인 자세',
    accIntensityLowMsg: '저강도 활동 (휴식, 수면)',
    accIntensityNormalMsg: '중저강도 활동 (일상 생활)',
    accIntensityHighMsg: '중고강도 활동 (운동, 작업)',
    accIntensityVeryHighMsg: '고강도 활동 (격렬한 운동)',
    accAvgMoveLowMsg: 'Stationary (정지 상태)',
    accAvgMoveNormalMsg: 'Sitting (앉기)',
    accAvgMoveHighMsg: 'Walking (걷기)',
    accAvgMoveVeryHighMsg: 'Running (달리기)',
    accMaxMoveLowMsg: 'Stationary (정지 상태)',
    accMaxMoveNormalMsg: 'Sitting (앉기)',
    accMaxMoveHighMsg: 'Walking (걷기)',
    accMaxMoveVeryHighMsg: 'Running (달리기)',
    // -- Shared Index UI --
    indexNoData: '데이터 없음',
    indexSampleAvg: '개 샘플 평균',
    indexDeviceRequired: '(디바이스 연결이 필요합니다.)',
    indexDetailPreparing: '상세 정보가 준비 중입니다.',
    indexDisclaimer: '본 앱은 의료기기가 아니며, 모든 측정값은 웰니스·연구 참고용입니다.',
    indexStabilized: '안정화',
    indexSampleCount: '개 샘플',
    // Tooltip
    tooltipFormula: '공식',
    tooltipNormalRange: '정상 범위',
    tooltipInterpretation: '해석',
    tooltipReference: '참고문헌',
    tooltipDescription: '설명',
    tooltipMeasurement: '측정 방법',
    tooltipClassification: '분류 기준',
    tooltipUnit: '단위',
    // -- 차트 공통 --
    powerSpectrumConnect: '연결 후 파워 스펙트럼을 실시간으로 확인할 수 있습니다',
    powerSpectrumAnalyzing: 'EEG 데이터 기반 주파수 스펙트럼 분석 중...',
    powerSpectrumFooter: 'EEG 데이터 기반 실시간 파워 스펙트럼 분석 • Morlet Wavelet 변환',
    bandPowerConnect: '연결 후 EEG 주파수 밴드 파워를 실시간으로 확인할 수 있습니다',
    bandPowerAccuracyDegrade: '주파수 분석 정확도가 저하될 수 있습니다',
    bandPowerDiff: '차이',
    bandPowerLabel: 'Power Spectrum 기반 실시간 주파수 밴드 분석',
    dataQualityLabel: '데이터 품질',
    spectrumLabel: 'Spectrum',
    leftBrainDominant: '좌뇌 우세',
    rightBrainDominant: '우뇌 우세',
    balancedState: '균형',
    tooltipTotalPower: '전체 파워',
    tooltipChannelPower: '채널별 파워',
    tooltipBrainDiff: '좌우뇌 차이',
    tooltipBandInterpretation: 'Power Spectrum 그래프의 {range} 구간을 평균한 값입니다.',
    systemInfo: '시스템 정보',
    connectionStateLabel: '연결 상태',
    ch1SQIDataLabel: 'Ch1 SQI 데이터',
    ch2SQIDataLabel: 'Ch2 SQI 데이터',
    eegSQIComputing: 'EEG 데이터 기반 SQI 계산 중...',
    graphUpdateInterval: '그래프 업데이트 주기',
    measuring: '측정 중...',
    // ACC 차트 제목
    accMagnitudeTitle: 'ACC 가속도 크기 (중력 제거)',
    accMagnitudeNoData: 'ACC 가속도 - 데이터 없음',
    accTooltipSample: '샘플',
    accTooltipMagnitude: '움직임 크기',
    accTooltipActivityState: '활동 상태',
    accTooltipGravityRemoved: '중력 제거',
    // PPG 차트 제목
    ppgFilteredTitle: 'PPG 밴드패스 필터링된 신호 (0.5-5.0Hz, Red & IR)',
    ppgNoDataTitle: 'PPG 신호 - 데이터 없음',
    ppgFilteredTooltip: '0.5-5.0Hz 밴드패스 필터링됨',
    ppgSQITitle: 'PPG 신호 품질 지수 (SQI)',
    tooltipSample: '샘플',
    normalRange: '정상 범위',
  },
  dataCenter: {
    title: 'Data Center',
    subtitle: '데이터 저장 및 관리',
    sessions: '세션',
    totalData: '총 데이터',
    storageType: '저장소 유형',
    startRecording: '레코딩 시작',
    stopRecording: '레코딩 중지',
    recording: '녹화 중',
    noSessions: '저장된 세션이 없습니다',
    noSessionsDesc: '레코딩을 시작하면 여기에 세션이 표시됩니다.',
    sessionName: '세션 이름',
    duration: '재생 시간',
    size: '크기',
    date: '날짜',
    delete: '삭제',
    download: '다운로드',
    upload: '업로드',
    uploadToCloud: '클라우드에 업로드',
    cloudStorage: '클라우드 저장소',
    localStorage: '로컬 저장소',
    incompleteSessions: '불완전한 세션',
    cleanup: '정리',
    storageLocation: '저장 위치',
    storageSubtitle: '데이터 저장소 및 관리',
    storageSettingsBtn: '저장소 설정',
    selectStorageDirectory: '저장소 디렉토리를 선택해주세요',
    selectStorageDirectoryDesc: '데이터 저장을 위해 로컬 저장소 디렉토리를 선택해야 합니다.',
    selectAction: '선택',
    storageConnected: '저장소 디렉토리 연결됨',
    totalStorage: 'Total Storage',
    used: 'Used',
    available: 'Available',
    realtimeStreamingRecording: '실시간 스트리밍 레코딩',
    deviceInfo: '디바이스 정보',
    connectedLinkBand: '연결된 LINK BAND 디바이스',
    deviceNameLabel: '디바이스명',
    deviceIdLabel: '디바이스 ID',
    connectionStatus: '연결 상태',
    connected: '연결됨',
    batteryLabel: '배터리',
    connectionTime: '연결 시간',
    samplingRateLabel: '샘플링 레이트',
    storageInfoCard: '저장소 정보',
    storageLocationLabel: '데이터 저장 위치',
    storagePath: '저장 경로',
    copyPath: '경로 복사',
    openFolder: '폴더 열기',
    connectDeviceTitle: '디바이스를 연결해주세요!',
    connectDeviceDesc: '데이터 수집 및 레코딩을 위해서는 LINK BAND 디바이스 연결이 필요합니다.',
    connectDeviceTip: 'Device Manager 탭에서 디바이스를 연결한 후 다시 시도해주세요.',
    recordingControl: '레코딩 제어',
    deviceConnected: '디바이스 연결됨',
    deviceNotConnected: '디바이스 연결 안됨',
    storageReady: '저장소 준비됨',
    storageSetupNeeded: '저장소 설정 필요',
    dataStreaming: '데이터 스트리밍 중',
    streamingStopped: '스트리밍 중지',
    fileSaving: '파일 저장 중',
    saveWaiting: '저장 대기',
    sessionNamePlaceholder: '세션 이름 (선택사항)',
    durationLabel: '지속 시간',
    dataSize: '데이터 크기',
    savingProgress: '저장 중...',
    stopRecordingShort: '레코딩 중지',
    startRecordingShort: '레코딩 시작',
    saveSettings: '저장 설정',
    indexedDbInfo: '💾 IndexedDB (브라우저 내장 저장소)',
    indexedDbDesc: '모든 데이터는 브라우저 IndexedDB에 저장되며, 다운로드 시 원하는 형식(CSV/JSON/ZIP)으로 변환됩니다.',
    dataTypesToSave: '저장할 데이터 타입',
    cannotDeleteActive: '현재 사용 중인 세션은 삭제할 수 없습니다',
    cannotDeleteActiveDesc: '세션 업로드 또는 레코딩이 완료될 때까지 기다려주세요.',
    sessionDeleted: '세션이 삭제되었습니다.',
    androidDeleteLimited: '안드로이드 환경에서는 완전한 파일 삭제가 제한됩니다. 세션이 목록에서 제거되었습니다.',
    sessionDeleteFailed: '세션 삭제에 실패했습니다.',
    androidAccessLimited: 'Android 환경에서는 파일 시스템 접근이 제한됩니다.',
    cannotDownloadActive: '현재 사용 중인 세션은 다운로드할 수 없습니다',
    cannotDownloadActiveDesc: '세션 업로드 또는 레코딩이 완료될 때까지 기다려주세요.',
    sessionDownloadFailed: '세션 다운로드에 실패했습니다.',
    cannotCopyPathActive: '현재 사용 중인 세션은 경로를 복사할 수 없습니다',
    cannotCopyPathActiveDesc: '세션 업로드 또는 레코딩이 완료될 때까지 기다려주세요.',
    pathCopied: '경로가 복사되었습니다',
    pathCopiedDesc: '세션의 절대 경로가 클립보드에 복사되었습니다.',
    pathCopyFailed: '경로 복사 실패',
    pathCopyFailedDesc: '경로를 클립보드에 복사하는 중 오류가 발생했습니다.',
    storagePathCopied: '저장소 경로가 복사되었습니다',
    storagePathCopiedDesc: '저장소 루트 경로가 클립보드에 복사되었습니다.',
    folderOpenFailed: '폴더 열기 실패',
    folderOpenFailedDesc: '폴더 경로를 복사하는 중 오류가 발생했습니다.',
    storageFolderPathCopied: '저장소 폴더 경로가 복사되었습니다',
    stopRecordingFailedAlert: '레코딩 중지에 실패했습니다.',
    connectDeviceFirstAlert: '디바이스를 먼저 연결해주세요.',
    selectStorageFirstAlert: '로컬 저장을 위해 저장소 디렉토리를 먼저 선택해주세요.',
    awsCredentialsAlert: 'S3 클라우드 저장을 위해 AWS 자격 증명을 설정해주세요.',
    startRecordingFailedAlert: '레코딩 시작에 실패했습니다.',
    storageRestoreError: '저장소 복원 오류',
    storageRestoreErrorDesc: '저장소 복원 중 오류가 발생했습니다.',
    allData: '전체 데이터',
    eegOnly: 'EEG만',
    ppgOnly: 'PPG만',
    accOnly: 'ACC만',
    today: '오늘',
    thisWeek: '이번 주',
    searchPlaceholder: '세션 이름 또는 디바이스로 검색...',
    advancedFilter: '고급 필터',
    savedSessions: '저장된 세션',
    newest: '최신순',
    oldest: '오래된순',
    colSessionName: '세션 이름',
    colDateTime: '날짜/시간',
    colDuration: '지속 시간',
    colDevice: '디바이스',
    colDataTypes: '데이터 타입',
    colSize: '크기',
    colLocation: '저장 위치',
    colActions: '작업',
    uploading: '업로드 중...',
    recordingInProgress: '레코딩 중...',
    copyAbsolutePath: '절대 경로 복사 (클립보드에 복사)',
    downloadSession: '세션 내려받기',
    deleteSession: '세션 삭제',
    noSessionsFound: '저장된 세션이 없습니다',
    noSessionsFoundDesc: '다른 검색어나 필터를 시도해보세요.',
    loadingSessions: '저장된 세션을 불러오는 중...',
    qualityExcellent: '우수',
    qualityGood: '양호',
    qualityFair: '보통',
    qualityPoor: '불량',
    folderWinTitle: 'Windows 탐색기에서 저장소 폴더 열기',
    folderWinDesc: 'Windows 탐색기에서 Ctrl+L을 눌러 복사된 경로로 이동하세요.',
    folderMacTitle: 'Finder에서 저장소 폴더 열기',
    folderMacDesc: 'Finder에서 ⌘+Shift+G를 눌러 복사된 경로로 이동하세요.',
    folderLinuxTitle: '파일 매니저에서 저장소 폴더 열기',
    folderLinuxDesc: '파일 매니저에서 Ctrl+L을 눌러 복사된 경로로 이동하세요.',
    folderGenericTitle: '파일 매니저에서 저장소 폴더 열기',
    folderGenericDesc: '파일 매니저에서 복사된 경로로 이동하세요.',
  },
  streamProcess: {
    title: 'Stream Process',
    subtitle: '실시간 브로드캐스팅 모니터링',
    broadcastServer: '브로드캐스트 서버',
    serverUrl: '서버 URL',
    connect: '연결',
    disconnect: '해제',
    connected: '연결됨',
    notConnected: '연결 안됨',
    healthCheck: 'Health Check',
    deviceStatus: '디바이스 상태',
    deviceName: '디바이스명',
    deviceId: '디바이스 ID',
    connectionStatus: '연결 상태',
    battery: '배터리',
    noDeviceAlert: '디바이스 미연결 - Mock 모드로 테스트 가능합니다',
    mode: '모드',
    mockMode: 'Mock 모드',
    broadcastId: '브로드캐스트 ID',
    mockHint: '💡 디바이스 없이도 Mock 데이터로 브로드캐스팅을 테스트할 수 있습니다.',
    streamingControl: '스트리밍 제어',
    sent: '전송됨',
    received: '수신됨',
    subscribers: '구독자',
    avgResponse: '평균 응답',
    startSubscription: '구독 시작',
    stopSubscription: '구독 중지',
    viewStats: '통계 조회',
    mockSimulation: 'Mock 시뮬레이션',
    mockSimulationDesc: '테스트 데이터를 전송하여 브로드캐스팅을 테스트합니다',
    eegRawLabel: 'EEG Raw (250 샘플)',
    ppgRawLabel: 'PPG Raw (50 샘플)',
    accRawLabel: 'ACC Raw (30 샘플)',
    analysisMetricsLabel: 'Analysis Metrics',
    combinedDataLabel: '복합 데이터 (All)',
    continuousStreamLabel: '연속 스트림 (10초)',
    stopSimulationLabel: '시뮬레이션 중지',
    serverStats: '서버 통계',
    messagesLabel: '수신된 메시지',
    messages: '메시지',
    seconds: '초',
    noMessages: '수신된 메시지가 없습니다',
    noMessagesDesc: '구독을 시작하거나 시뮬레이션을 실행하세요',
    clear: 'Clear',
    close: '닫기',
    developerApi: 'Developer API - 외부 서비스 연동 가이드',
    developerApiDesc: '외부 서비스나 애플리케이션에서 실시간 센서 데이터를 구독하는 방법입니다. SSE (Server-Sent Events)를 사용하여 간단하게 연동할 수 있습니다.',
    sseSubscribe: 'SSE로 데이터 구독',
    copy: '복사',
    messageStructure: '메시지 데이터 구조',
    optionalFieldsNote: '💡 모든 필드는 선택적(optional)입니다. 전송하는 쪽에서 필요한 데이터만 포함하여 전송합니다.',
    apiEndpoints: 'API 엔드포인트',
    subscribeEndpointDesc: 'SSE 스트림으로 실시간 데이터 수신',
    publishEndpointDesc: '데이터 발행 (현재 브라우저에서 자동 전송)',
    healthEndpointDesc: '서버 상태 확인',
    statsEndpointDesc: '서버 통계 조회',
    quickTest: '빠른 테스트',
    quickTestTerminal: '터미널에서 바로 테스트:',
    quickTestDesc: '위 명령어를 터미널에서 실행한 후, 이 페이지에서 Mock 데이터를 전송하면 터미널에서 실시간으로 데이터를 볼 수 있습니다.',
    pythonSSEClient: 'Python SSE Client',
    curlTest: 'cURL로 테스트',
    serverHealthCheck: '서버 Health Check',
    serverStatsQuery: '서버 통계 조회',
    curlBufferNote: '-N 옵션은 버퍼링을 비활성화하여 실시간으로 데이터를 받습니다.',
    status: '상태',
    uptime: '가동 시간',
    activeDevice: '활성 디바이스',
    totalConnections: '총 연결 수',
    libRequired: '필요한 패키지:',
    connectedToast: '브로드캐스트 서버에 연결되었습니다',
    connectedMockDesc: 'Mock 모드로 연결됨',
    connectFailed: '서버 연결 실패',
    unknownError: '알 수 없는 오류',
    disconnectedToast: '브로드캐스트 서버 연결이 해제되었습니다',
    healthOk: '서버 상태 확인 완료',
    healthFail: 'Health check 실패',
    statsOk: '서버 통계 조회 완료',
    statsFail: '통계 조회 실패',
    connectFirst: '서버에 먼저 연결해주세요',
    dataSent: '데이터 전송 완료',
    dataSentDesc: '{count}명의 구독자에게 전송됨 ({time}ms)',
    dataSendFail: '데이터 전송 실패',
    simulationComplete: '시뮬레이션 완료 (10초)',
    simulationStarted: '연속 스트림 시뮬레이션 시작 (10초)',
    simulationStopped: '시뮬레이션 수동 중지됨',
    subscriptionStarted: '구독 시작됨',
    subscriptionStopped: '구독 중지됨',
    copyJSToast: 'JavaScript 코드가 복사되었습니다',
    copyPythonToast: 'Python 코드가 복사되었습니다',
    copyCurlToast: 'cURL 명령어가 복사되었습니다',
    copyCmdToast: '명령어가 복사되었습니다',
  },
  settings: {
    title: 'Settings',
    subtitle: '애플리케이션 설정을 관리합니다',
    deviceManager: 'Device Manager',
    deviceManagerDesc: '디바이스 연결 및 관리 설정',
    autoConnect: '자동 연결 기능',
    autoConnectDesc: '스캔된 디바이스를 자동으로 연결합니다.',
    autoRegister: '자동 등록 기능',
    autoRegisterDesc: '디바이스 연결 시 자동으로 등록하여 다음 연결을 더 빠르게 합니다.',
    deviceManagerInfo: '💡 디바이스 관리 기능 안내:',
    autoConnectInfo: '자동 연결: 스캔된 디바이스를 자동으로 연결합니다',
    autoRegisterInfo: '자동 등록: 연결된 디바이스를 자동으로 등록하여 다음 연결을 빠르게 합니다',
    manualModeInfo: '두 기능 모두 비활성화하면 완전 수동 모드로 작동합니다',
    noDuplicateInfo: '이미 등록된 디바이스는 중복 등록되지 않습니다',
    timeZone: 'Time Zone',
    timeZoneDesc: '시간 표시 기준 설정',
    timeZoneLabel: '시간 표시 기준',
    timeZoneSystem: '시스템 시간대',
    timeZoneKorea: '한국 시간 (KST/GMT+9)',
    timeZoneUtc: '국제 표준시 (UTC)',
    timeZoneNote: '세션 시작/종료 시간과 모든 시간 표시에 적용됩니다',
    timeZoneInfo: '💡 시간대 설정 안내:',
    timeZoneSystemInfo: '시스템 시간대: 현재 컴퓨터의 시간대를 사용합니다',
    timeZoneKoreaInfo: '한국 시간: 항상 한국 시간(KST, UTC+9)으로 표시합니다',
    timeZoneUtcInfo: '국제 표준시: UTC 시간으로 표시합니다',
    timeZoneApplyInfo: '설정 변경 후 새로 시작하는 세션부터 적용됩니다',
    visualizer: 'Visualizer',
    visualizerDesc: '데이터 시각화 설정',
    visualizerComingSoon: '시각화 설정은 준비 중입니다.',
    language: 'Language',
    languageDesc: '인터페이스 언어 설정',
    languageKo: '한국어',
    languageEn: 'English',
  },
  documents: {
    title: '문서',
    subtitle: 'SDK 개발 관련 문서를 조회하고 볼 수 있습니다.',
    searchPlaceholder: '문서 검색...',
    browseDocs: '문서 둘러보기',
    browseDocsDesc: '필요한 정보를 주제별로 찾아보세요.',
    viewDocs: '문서 보기',
    contents: '목차',
    docs: '문서',
    previous: '이전',
    next: '다음',
    selectDoc: '문서를 선택해주세요',
    selectDocDesc: '왼쪽 사이드바에서 문서를 선택하여 내용을 확인하세요.',
    sdkDocumentation: 'SDK 문서',
    sdkHeroDesc: 'Web Bluetooth로 LINK BAND 2.0에 연결하고 EEG·PPG·ACC 생체신호를 실시간으로 수집·분석하는 개발자 가이드입니다.',
    menuLabel: '메뉴 열기',
    sectionDesc: {
      overview: 'LINK BAND SDK의 아키텍처와 핵심 개념',
      gettingStarted: '설치부터 첫 연결까지 빠르게 시작하기',
      bleProtocol: 'BLE 서비스 UUID, 패킷 구조, 타임스탬프 명세',
      apiReference: 'bluetoothService, StreamProcessor 등 API 레퍼런스',
      dataGuide: 'EEG/PPG/ACC 데이터 포맷과 내보내기 가이드',
      interface: 'Dashboard, Device Manager 등 인터페이스 가이드',
    },
  },
  exportModal: {
    title: '세션 내보내기',
    fileSize: '파일 크기',
    formatSelection: '내보내기 형식 선택',
    recommended: '권장',
    cancel: '취소',
    selectLocation: '저장 위치 선택',
    saveMethodTitle: '저장 방식 선택',
    saveToDevice: '기기 내부 저장',
    saveToDeviceDesc: '기기 내부 저장소(파일 앱)에 파일로 저장',
    shareToApps: '공유 시트로 전송',
    shareToAppsDesc: '다른 앱으로 공유',
    saveLocalSuccessDesc: '파일이 기기 내부 저장소에 저장되었습니다.',
    locationSelecting: '저장 위치 선택 중...',
    locationSelectingDesc: '브라우저에서 파일을 저장할 폴더를 선택해주세요.',
    progressTitle: '내보내기 진행 중...',
    progressZip: '폴더를 생성하고 파일을 복사하고 있습니다.',
    progressAll: '모든 파일을 개별적으로 내보내고 있습니다.',
    progressFormat: (format: string) => `${format} 파일을 내보내고 있습니다.`,
    progressLabel: '진행률',
    successTitle: '내보내기 완료!',
    successDesc: '세션 데이터가 성공적으로 내보내졌습니다.',
    done: '완료',
    errorTitle: '내보내기 실패',
    retry: '다시 시도',
    unknownError: '알 수 없는 오류가 발생했습니다.',
    fmtZipName: '전체 세션 (폴더)',
    fmtZipDesc: '모든 파일과 메타데이터를 폴더 형태로 내보내기',
    fmtCsvName: 'CSV 파일만',
    fmtCsvDesc: '스프레드시트에서 열 수 있는 CSV 형식',
    fmtJsonName: 'JSON 파일만',
    fmtJsonDesc: '구조화된 JSON 형식',
    fmtJsonlName: 'JSON Lines 파일만',
    fmtJsonlDesc: '스트리밍에 최적화된 JSONL 형식',
    fmtBinaryName: 'Binary 파일만',
    fmtBinaryDesc: '가장 압축된 바이너리 형식',
    fmtAllName: '모든 파일 (개별)',
    fmtAllDesc: '모든 형식의 파일을 개별적으로 내보내기',
    sizeCompressed50: '~50% 압축',
    sizeOriginal70: '~70% 원본',
    sizeOriginal65: '~65% 원본',
    sizeCompressed30: '~30% 압축',
  },
  sessionManager: {
    recordingControl: '📊 세션 녹화 제어',
    sessionNameOptional: '세션 이름 (선택사항)',
    startRecording: '세션 녹화 시작',
    stopRecording: '세션 녹화 중지',
    recordingPrefix: '🔴 녹화 중',
    duration: '지속 시간',
    dataLabel: '데이터',
    liveBadge: 'LIVE',
    connectDeviceFirst: '디바이스를 먼저 연결하세요',
    deviceNotConnectedAlert: '디바이스가 연결되지 않았습니다.',
    currentSession: '현재 세션',
    inProgress: '진행 중',
    sessionName: '세션 이름',
    startTime: '시작 시간',
    device: '디바이스',
    dataCollection: '데이터 수집',
    exportAllZip: '모든 데이터 ZIP으로 받기',
    exportEeg: 'EEG 내보내기',
    exportPpg: 'PPG 내보내기',
    exportAcc: 'ACC 내보내기',
    exportSystemLog: '시스템 로그 내보내기',
    storageInfo: '저장소 정보',
    storedSessions: '저장된 세션',
    used: '사용 중',
    available: '사용 가능',
    usageRate: '저장소 사용률',
    refresh: '새로고침',
    noSavedSessions: '저장된 세션이 없습니다',
    savedSessionsCount: (count: number) => `저장된 세션 (${count}개)`,
    colSession: '세션',
    colDevice: '디바이스',
    colType: '유형',
    colDate: '날짜',
    colDuration: '지속시간',
    colSamples: '샘플 수',
    colSize: '크기',
    colStatus: '상태',
    colActions: '동작',
    statusCompleted: '완료됨',
    statusProcessing: '처리 중',
    exportDialogTitle: '데이터 내보내기',
    exportDialogSessionLabel: '세션',
    exportDialogSelect: '내보낼 데이터 유형을 선택하세요',
    zipDescription: '모든 데이터와 세션 정보를 하나의 ZIP 파일로 다운로드합니다',
    eegDataCount: (n: number) => `EEG 데이터 (${n.toLocaleString()}개)`,
    ppgDataCount: (n: number) => `PPG 데이터 (${n.toLocaleString()}개)`,
    accDataCount: (n: number) => `가속도계 데이터 (${n.toLocaleString()}개)`,
    systemLogLabel: '시스템 로그',
    previous: '이전',
    next: '다음',
    sessionRangeOf: (start: number, end: number, total: number) => `${start}-${end} / ${total}개 세션`,
    confirmDelete: '이 세션을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
    csvNotFoundAlert: 'CSV 형식의 파일을 찾을 수 없습니다.',
    exportFailedAlert: '세션 내보내기 실패: csv 형식의 파일을 찾을 수 없습니다.',
    exportButton: '내보내기',
  },
  brainStateIndicator: {
    title: '뇌파 상태 인디케이터',
    deviceRequired: '디바이스 연결 필요',
    sensorContactRequired: '센서 접촉 상태 확인 필요',
    instruction: 'LINK BAND 디바이스를 연결하고 센서를 올바르게 접촉시켜 주세요',
    movingAverage: 'Moving Average 기반 안정화',
    footerNote: '실시간 뇌파 데이터 기반 • 15개 샘플 Moving Average 안정화',
    focus: '집중력',
    relaxation: '이완도',
    stress: '스트레스',
    attention: '주의력',
    meditation: '명상 수준',
    cognitiveLoad: '인지 부하',
    levelLow: '낮음',
    levelMedium: '보통',
    levelHigh: '높음',
  },
  performanceMonitor: {
    title: '성능 모니터',
    notConnected: '연결 안됨',
    statusGood: '양호',
    statusWarning: '주의',
    statusCritical: '위험',
    statusUnknown: '알 수 없음',
    processingTime: '처리 시간',
    memoryUsage: '메모리 사용량',
    bufferSize: '버퍼 크기',
    droppedPackets: '드롭된 패킷',
    timestampSync: '타임스탬프 동기화',
    timestampDrift: '타임스탬프 드리프트',
    packetsUnit: '개',
  },
  browserCompatibility: {
    title: '브라우저 호환성 상태',
    currentBrowser: (browser: string, platform: string) => `현재 브라우저: ${browser} on ${platform}`,
    bluetoothAPILabel: 'Web Bluetooth API',
    supported: '지원',
    unsupported: '미지원',
    secure: '안전',
    insecure: '불안전',
    securityContext: '보안 컨텍스트 (HTTPS)',
    unsupportedAlert: '현재 브라우저에서는 LINK BAND 디바이스 연결을 지원하지 않습니다.',
    iOSUserGuide: 'iOS 사용자 안내',
    iOSSafariDesc: 'iOS Safari는 Web Bluetooth를 지원하지 않지만, 대안이 있습니다!',
    capacitorNativeTitle: '✅ LINK BAND SDK 네이티브 앱',
    capacitorNativeDesc: '네이티브 BLE 플러그인으로 LINK BAND 디바이스에 직접 연결합니다.',
    bluefyTitle: '✅ 권장 방법: Bluefy 브라우저',
    bluefyDesc: 'Web Bluetooth를 지원하는 전용 iOS 브라우저입니다.',
    downloadBluefy: 'App Store에서 Bluefy 다운로드',
    webBLETitle: '🔧 대안: WebBLE 브라우저',
    webBLEDesc: '또 다른 Web Bluetooth 지원 브라우저입니다.',
    downloadWebBLE: 'WebBLE 브라우저 다운로드',
    howToUse: '⚡ 사용 방법',
    step1: '1. 위 앱 중 하나를 다운로드',
    step2: '2. 앱에서 sdk.linkband.store 접속',
    step3: '3. LINK BAND 디바이스 연결 및 사용',
    androidUserGuide: 'Android 사용자 안내',
    androidSupportedMsg: '현재 브라우저에서 LINK BAND 연결이 가능합니다!',
    androidChromeMsg: 'Chrome 또는 Edge 브라우저를 사용해주세요.',
    androidReadyTitle: '✅ 연결 준비 완료',
    androidReadyDesc: 'LINK BAND 페이지로 이동하여 디바이스를 연결하세요.',
    androidBrowserTitle: '🔧 권장 브라우저',
    androidBrowserDesc: 'Chrome 또는 Edge 브라우저에서 이 사이트를 열어주세요.',
    downloadChrome: 'Chrome 브라우저 다운로드',
    desktopUserGuide: '데스크톱 사용자 안내',
    desktopSupportedMsg: '현재 브라우저에서 LINK BAND 연결이 가능합니다!',
    desktopBrowserMsg: 'Chrome, Edge, 또는 Opera 브라우저를 사용해주세요.',
    desktopReadyTitle: '✅ 연결 준비 완료',
    desktopReadyDesc: 'LINK BAND 페이지로 이동하여 디바이스를 연결하세요.',
    desktopBrowserTitle: '🔧 지원 브라우저',
    supportedBrowsers: '다음 브라우저에서 Web Bluetooth를 지원합니다:',
    showDetails: '상세 정보 보기',
    hideDetails: '상세 정보 숨기기',
    technicalDetails: '기술 세부사항',
  },
  mobileStorageGuide: {
    title: '안드로이드 데이터 저장 안내',
    description: '안드로이드 환경에서의 데이터 저장 방식에 대한 안내입니다.',
    autoDownloadTitle: '자동 다운로드 방식',
    autoDownloadDesc: '안드로이드에서는 데이터가 자동으로 다운로드 폴더에 저장됩니다.',
    currentSession: '현재 세션',
    eegData: (n: number) => `EEG 데이터: ${n}개`,
    ppgData: (n: number) => `PPG 데이터: ${n}개`,
    accData: (n: number) => `ACC 데이터: ${n}개`,
    howItWorks: '작동 방식',
    autoSave30s: '레코딩 중 자동으로 30초마다 CSV 파일 생성',
    autoSave50MB: '메모리 절약을 위해 50MB마다 자동 저장',
    finalSave: '레코딩 종료 시 최종 데이터 저장',
    checkDownloads: '다운로드 폴더에서 파일 확인 가능',
    savedFiles: '저장되는 파일',
    openDownloads: '다운로드 폴더 열기',
    confirm: '확인',
    note: '참고',
    noteTimestamp: '파일명에는 타임스탬프가 포함되어 구분이 용이합니다.',
    noteTransfer: '파일을 컴퓨터로 전송하여 분석하거나 백업할 수 있습니다.',
    manualCheck: '다운로드 폴더를 수동으로 확인해주세요.\n\n파일 관리자 → 다운로드 폴더에서 LINK BAND 데이터를 찾을 수 있습니다.',
  },
};

// 영어 번역
const en: TranslationKeys = {
  common: {
    manage: 'Manage',
    settings: 'Settings',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    loading: 'Loading...',
    noData: 'No data',
    notAvailable: '—',
    debugInfo: 'Debug Info',
    on: 'On',
    off: 'Off',
    close: 'Close',
  },
  pwaUpdate: {
    title: 'New Version Update',
    newVersionAvailable: 'A new version of the LINK BAND SDK is available',
    updateNow: 'Update Now',
    updating: 'Updating...',
    later: 'Later',
  },
  sidebar: {
    sections: { workspace: 'Workspace', reference: 'Reference' },
    dashboard: 'Dashboard',
    deviceManager: 'Device Manager',
    visualizer: 'Visualizer',
    dataCenter: 'Data Center',
    streamProcess: 'Stream Process',
    documents: 'Documents',
    settings: 'Settings',
    deviceConnected: 'Device connected',
    sdkReady: 'SDK runtime ready',
    webBluetooth: 'web bluetooth · ble',
    closeLabel: 'Close sidebar',
    expandLabel: 'Expand sidebar',
    collapseLabel: 'Collapse sidebar',
  },
  topbar: {
    menuLabel: 'Open menu',
    dashboard: 'Dashboard',
    noDevice: 'No device',
  },
  statusbar: {
    connection: 'Connection',
    connected: 'Connected',
    disconnected: 'Disconnected',
    streaming: 'Streaming',
    recording: 'Recording',
    battery: 'Battery',
    samplingRate: 'Sampling Rate',
    dataSize: 'Data Size',
    elapsed: 'Elapsed',
    sensors: 'Sensors',
    goodContact: 'Good Contact',
    poorContact: 'Poor Contact',
    stopped: 'Stopped',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'LINK BAND SDK Dashboard',
    connected: 'LINK BAND Connected',
    waiting: 'Waiting for connection',
    linkBandStatus: 'LINK BAND Status',
    linkBandStatusSub: 'Connection · Battery · Sampling Rate',
    goToLinkBand: 'Go to LINK BAND',
    dataVisualization: 'Data Visualization',
    dataVisualizationSub: 'Real-time EEG · PPG Preview',
    connectPlease: 'Please connect a device',
    goToVisualizer: 'Go to Visualizer',
    dataStorage: 'Data Storage',
    dataStorageSub: 'IndexedDB · Browser Built-in',
    settings: 'Settings',
    storageReady: 'Storage ready',
    goToDataCenter: 'Go to Data Center',
    connection: 'Connection',
    connectionDuration: 'Duration:',
    connectFromDeviceManager: 'Connect from Device Manager',
    recording: 'Recording',
    recordingDuration: 'Recording duration:',
  },
  deviceManager: {
    title: 'Device Manager',
    subtitle: 'BLE device scanning & connection management',
    pageHeading: 'Device Manager',
    pageSubheading: 'Manage your LINK BAND devices',
    scan: 'Start Scan',
    scanning: 'Scanning...',
    scanDevices: 'Scan Devices',
    demoTry: 'Try Demo',
    demoTryHint: 'No device? Explore the app with a virtual LINK BAND.',
    demoBannerTitle: 'Demo Mode',
    demoBannerDesc: 'Simulated data, not from a real device. Not for measurement or diagnosis.',
    demoExit: 'Exit Demo',
    demoStartFailed: 'Failed to start demo mode.',
    refresh: 'Refresh',
    connect: 'Connect',
    connecting: 'Connecting...',
    disconnect: 'Disconnect',
    disconnecting: 'Disconnecting...',
    forceDisconnect: 'Force Disconnect',
    connected: 'Connected',
    disconnected: 'Disconnected',
    noDevices: 'No devices found',
    noDevicesDesc: 'Start a scan to find BLE devices.',
    deviceFound: 'device(s) found',
    deviceName: 'Device Name',
    deviceId: 'Device ID',
    rssi: 'Signal Strength',
    registered: 'Registered',
    notRegistered: 'Not Registered',
    register: 'Register',
    unregister: 'Unregister',
    rename: 'Rename',
    storageSettings: 'Storage Settings',
    battery: 'Battery',
    firmwareVersion: 'Firmware Version',
    lastConnected: 'Last Connected',
    autoConnect: 'Auto Connect',
    autoConnectDesc: 'Automatically connect to scanned devices.',
    autoRegister: 'Auto Register',
    autoRegisterDesc: 'Automatically register connected devices.',
    browserNotSupported: 'Browser does not support Web Bluetooth',
    browserNotSupportedDesc: 'Please use a modern browser like Chrome or Edge.',
    bleNotAvailable: 'Bluetooth is not available',
    bleNotAvailableDesc: 'Please check if Bluetooth is enabled on your device.',
    modalClose: 'Close',
    confirmDisconnect: 'Confirm Disconnect',
    confirmDisconnectDesc: 'Are you sure you want to disconnect?',
    iosBrowserGuide: 'iOS Browser Guide',
    iosBrowserGuideMsg: 'On iOS, please use Bluefy or WebBLE browser. They are available on the App Store.',
    bluetoothNotSupported: 'Bluetooth Not Supported',
    bluetoothNotSupportedMsg: 'Please use Chrome or Edge browser.',
    scanComplete: 'Scan Complete',
    scanCompleteMsg: 'device(s) found',
    scanFailed: 'Scan Failed',
    scanFailedMsg: 'Device scan failed',
    deviceConnectedTitle: 'Device Connected',
    deviceConnectedMsg: 'Successfully connected to device',
    deviceAutoRegistered: 'Device Auto-Registered',
    deviceAutoRegisteredMsg: 'has been auto-registered.',
    autoRegistrationFailed: 'Auto-Registration Failed',
    autoRegistrationFailedMsg: 'Failed to auto-register device',
    connectionFailed: 'Connection Failed',
    connectionFailedMsg: 'Failed to connect to device',
    connectionFailedPairedHint: 'If LINK BAND is paired in your macOS/Windows Bluetooth settings, remove (forget) it there, power-cycle the band, and try again.',
    deviceDisconnected: 'Device Disconnected',
    deviceDisconnectedMsg: 'Device has been disconnected',
    disconnectionFailed: 'Disconnection Failed',
    disconnectionFailedMsg: 'Failed to disconnect device',
    forceDisconnectComplete: 'Force Disconnect Complete',
    forceDisconnectCompleteMsg: 'Device has been forcefully disconnected. You can reconnect now.',
    forceDisconnectFailed: 'Force Disconnect Failed',
    forceDisconnectFailedMsg: 'Force disconnect failed. Please refresh the browser.',
    deviceRegistered: 'Device Registered',
    deviceRegisteredMsg: 'has been registered successfully',
    registrationFailed: 'Registration Failed',
    registrationFailedMsg: 'Failed to register device',
    deviceUnregistered: 'Device Unregistered',
    deviceUnregisteredMsg: 'Device has been removed from registered devices',
    unregistrationFailed: 'Unregistration Failed',
    unregistrationFailedMsg: 'Failed to unregister device',
    deviceRenamed: 'Device Renamed',
    deviceRenamedMsg: 'Device nickname has been changed.',
    renameFailed: 'Rename Failed',
    renameFailedMsg: 'Failed to rename device',
    nicknameTooLong: 'Device nickname cannot exceed 20 characters.',
    systemInitFailed: 'System Initialization Failed',
    systemInitFailedMsg: 'Failed to initialize system',
    manageDevices: 'Manage your LINK BAND devices',
    availableDevices: 'Available Devices',
    registeredDevices: 'Registered Devices',
    iosUserGuide: '🍎 iOS User Guide',
    androidUserGuide: '🤖 Android User Guide',
    desktopUserGuide: '💻 Desktop User Guide',
    iosSafariNotSupported: 'iOS Safari does not support Web Bluetooth. Please use one of the following browsers:',
    iosRecommendedBrowser: 'Recommended browser (download from App Store)',
    iosAlternativeBrowser: 'Alternative browser (download from App Store)',
    iosDownloadInstruction: 'After installing the app, visit:',
    devicePrepTitle: 'LINK BAND device connection prep:',
    devicePrepPower: 'Turn on your LINK BAND device',
    devicePrepPairing: 'Set it to pairing mode',
    devicePrepClose: 'Keep the device nearby',
    devicePrepBrowserAndroid: 'Use Chrome or Edge browser',
    devicePrepBrowserDesktop: 'Use Chrome, Edge, or Opera browser',
    deviceConnecting: 'Connecting to device...',
    deviceConnectingDesc: 'Attempting to connect to your LINK BAND device. Please wait.',
    connectionFailedTitle: 'Connection Failed',
    unknownError: 'An unknown error occurred',
    solution: 'Solutions:',
    solutionPower: 'Try turning the device off and on again',
    solutionPairing: 'Re-enable pairing mode',
    solutionRefresh: 'Refresh the browser and try again',
    realtimeReceiving: 'Receiving real-time EEG data',
    live: 'Live',
    disconnectingShort: 'Disconnecting...',
    disconnectShort: 'Disconnect',
    forceDisconnectShort: 'Force Disconnect',
    chargingLabel: '⚡ Charging',
    dischargingLabel: '🔋 Discharging',
    scanningShort: 'Scanning...',
    scanningForDevices: 'Scanning for devices...',
    pairingHint: 'Make sure your LINK BAND is in pairing mode',
    available: 'Available',
    connectedStatus: 'Connected',
    autoConnectWaiting: 'Auto-connect waiting',
    noAvailableDevices: 'No available devices',
    noAvailableDevicesDesc: 'Select LINK BAND in the browser pairing window to connect automatically',
    scanForDevices: 'Scan for Devices',
    autoConnectShort: 'Auto Connect',
    autoRegisterShort: 'Auto Register',
    noRegisteredDevices: 'No registered devices',
    noRegisteredDevicesDesc: 'Devices will be auto-registered when scanned and connected',
    connectionCount: 'Connections',
    lastShort: 'Last',
    registeredOn: 'Registered on',
    idLabel: 'ID',
    viewDetails: 'View Details',
    storageSettingsShort: 'Storage Settings',
    unregisterShort: 'Unregister',
    registerYourDevice: 'Register your device',
    registerYourDeviceDesc: 'Get faster connections next time',
    registerConfirmText: 'Would you like to register?',
    deviceNickname: 'Device Nickname',
    nicknameOptional: 'Device Nickname (optional)',
    nicknameMaxLength: 'Device Nickname (max 20 characters)',
    nicknamePlaceholder: 'Enter device nickname',
    nicknameHelp: 'Setting a nickname helps you easily identify devices in the list',
    nicknameEmptyHelp: 'If left empty, the default name will be used',
    skip: 'Skip',
    registerAction: 'Register',
    deviceNicknameChange: 'Change Device Nickname',
    deviceNicknameChangeDesc: 'Set a nickname to easily identify your device',
    renameConfirmText: 'Would you like to change the nickname?',
    cancelAction: 'Cancel',
    saveAction: 'Save',
  },
  visualizer: {
    title: 'Visualizer',
    subtitle: 'Real-time Data Visualization',
    deviceRequired: 'Device Connection Required',
    deviceRequiredDesc: 'Please connect a LINK BAND device for data visualization.',
    connectLinkBand: 'Connect Link Band',
    streaming: 'Streaming',
    waiting: 'Waiting',
    noSensorData: 'No sensor data',
    noSensorDataDesc: 'Real-time analysis results will appear once streaming starts.',
    receivingDataDesc: 'Receiving data...',
    startStreamingDesc: 'Real-time analysis results will appear once streaming starts.',
    eegBrainwave: 'EEG Brainwave',
    ppgHeartRate: 'PPG Heart Rate',
    accAcceleration: 'ACC Acceleration',
    initializing: 'Initializing system...',
    eegFilteredTitle: 'Real-time Filtered EEG Signal',
    ppgFilteredTitle: 'Real-time Filtered PPG Signal',
    connectDevice: 'Please connect a device',
    wearProperly: 'Please wear the device properly',
    redChannel: 'Red',
    irChannel: 'IR',
    deltaBand: 'Delta',
    thetaBand: 'Theta',
    alphaBand: 'Alpha',
    betaBand: 'Beta',
    gammaBand: 'Gamma',
    xAxisTime: 'Time (s)',
    xAxisFrequency: 'Frequency (Hz)',
    yAxisVoltage: 'Voltage (μV)',
    yAxisPower: 'Power (dB)',
    yAxisSignalQuality: 'Signal Quality (%)',
    ch1SeriesFP1: 'Channel 1 (FP1)',
    ch2SeriesFP2: 'Channel 2 (FP2)',
    goToVisualizer: 'Go to Visualizer',
    poorContact: 'Poor electrode contact',
    signalQualityMayDegrade: 'Signal quality analysis accuracy may degrade',
    poorContactDetection: 'Poor Electrode Contact Detected',
    contactNormal: 'Normal',
    contactPoor: 'Poor',
    eegAnalysisIndex: 'EEG Analysis Index',
    stabilizing: 'Stabilizing',
    eegTitle: 'EEG Brainwave Analysis',
    eegDesc: 'Visualizes real-time brainwave signal processing and analysis results.',
    ppgTitle: 'PPG Heart Rate Analysis',
    ppgDesc: 'Visualizes real-time PPG signal processing and heart rate variability analysis results.',
    accTitle: 'ACC Acceleration Analysis',
    accDesc: 'Visualizes real-time 3-axis accelerometer signal processing and movement pattern analysis.',
    ch1FilteredEEG: 'Ch1 Filtered EEG Signal (FP1)',
    ch2FilteredEEG: 'Ch2 Filtered EEG Signal (FP2)',
    ch1FilteredDesc: 'Channel 1 (FP1) - 60Hz notch + 1-45Hz bandpass filter applied',
    ch2FilteredDesc: 'Channel 2 (FP2) - 60Hz notch + 1-45Hz bandpass filter applied',
    ch1SQI: 'Ch1 Signal Quality Index (SQI)',
    ch2SQI: 'Ch2 Signal Quality Index (SQI)',
    sqiDesc: 'Monitors electrode contact status and signal quality',
    powerSpectrum: 'Power Spectrum (1-45Hz)',
    powerSpectrumDesc: 'Ch1, Ch2 frequency domain brainwave signal analysis',
    bandPower: 'Frequency Band Power',
    bandPowerDesc: 'Real-time Delta, Theta, Alpha, Beta, Gamma analysis',
    eegIndexes: 'EEG Analysis Indexes',
    eegIndexesDesc: 'Real-time Focus, Relaxation, Stress and 9 indexes',
    filteredPPG: 'Filtered PPG Signal',
    filteredPPGDesc: '0.5-5.0Hz bandpass filter applied to Red/IR LED signals (DC removed)',
    ppgSQI: 'PPG Signal Quality Index (SQI)',
    ppgSQIDesc: 'Monitors signal quality and electrode contact status',
    hrvIndexes: 'Heart Rate Variability Analysis',
    hrvIndexesDesc: 'Real-time Heart Rate, HRV, Stress and 6 indexes',
    accMagnitude: 'Acceleration Magnitude',
    accMagnitudeDesc: 'Real-time X, Y, Z axis acceleration magnitude monitoring',
    accIndexes: 'Comprehensive Analysis',
    accIndexesDesc: 'Activity, posture changes, movement pattern analysis',
    noGraphData: 'No data to display',
    connectToView: 'Connect device to view {channel} signal in real-time',
    channel1: 'Channel 1',
    channel2: 'Channel 2',
    brainwave: 'Brainwave',
    // -- EEG Indexes --
    eegFocusLabel: 'Focus Level',
    eegArousalLabel: 'Arousal Level',
    eegStressLabel: 'Stress Level',
    eegBalanceLabel: 'Hemispheric Balance',
    eegCognitiveLoadLabel: 'Cognitive Load',
    eegValenceLabel: 'Emotional Stability (Valence)',
    eegTotalPowerLabel: 'Neural Activity',
    eegTotalPowerDesc: 'Total brainwave activity intensity',
    eegCognitiveLoadDesc: 'Cognitive load based on theta/alpha ratio',
    eegBalanceDesc: 'Hemispheric activity balance indicator',
    eegStressDesc: 'High-frequency activity based stress',
    eegIndexNoData: 'No data',
    eegSampleAvg: 'samples avg',
    eegConnectToView: 'Connect device to view EEG analysis indices in real-time',
    eegAnalyzing: 'Calculating indices from processedDataStore EEG analysis...',
    eegAccuracyDegrade: 'EEG index accuracy may degrade',
    eegDeviceRequired: '(Device connection required)',
    // EEG Status Messages
    eegFocusLowMsg: 'Attention deficit or drowsiness',
    eegFocusHighMsg: 'Excessive focus or stress',
    eegFocusNormalMsg: 'Normal range',
    eegArousalLowMsg: 'Tension and stress state',
    eegArousalHighMsg: 'Excessive relaxation',
    eegArousalNormalMsg: 'Normal tension state',
    eegStressVeryLowMsg: 'Very low stress',
    eegStressLowMsg: 'Low stress',
    eegStressNormalMsg: 'Normal range',
    eegStressHighMsg: 'High stress',
    eegStressVeryHighMsg: 'Very high stress value',
    eegBalanceMeasuringMsg: 'Measuring',
    eegBalanceRightMsg: 'Creative (right brain dominant)',
    eegBalanceLeftMsg: 'Logical (left brain dominant)',
    eegBalanceNormalMsg: 'Balanced state',
    eegCognitiveLowMsg: 'Low engagement',
    eegCognitiveOverloadMsg: 'Overload state',
    eegCognitiveHighMsg: 'High cognitive load',
    eegCognitiveNormalMsg: 'Optimal load',
    eegValenceLowMsg: 'Emotional instability, excessive arousal',
    eegValenceHighMsg: 'Emotional blunting, excessive inhibition',
    eegValenceNormalMsg: 'Normal range',
    eegPowerHighMsg: 'Excessive neural activity',
    eegPowerLowMsg: 'Suppressed neural activity',
    eegPowerNormalMsg: 'Normal range',
    // -- PPG Indexes --
    ppgHrvTitle: 'HRV Analysis Indices',
    ppgSignalQualityNeed: 'Signal quality improvement needed',
    ppgStabilizing: 'Stabilizing',
    ppgStabilized: 'Stabilized',
    ppgConnectToView: 'Connect device to view HRV analysis indices in real-time',
    ppgAnalyzing: 'Calculating indices from processedDataStore PPG analysis...',
    ppgBpmDesc: 'Heart rate',
    ppgSpo2Desc: 'Blood oxygen saturation',
    ppgHrMaxDesc: 'Max HR during measurement (2 min)',
    ppgHrMinDesc: 'Min HR during measurement (2 min)',
    ppgStressIdxDesc: 'Stress index',
    ppgRmssdDesc: 'Root mean square of successive RR interval differences',
    ppgSdnnDesc: 'Standard deviation of NN intervals',
    ppgSdsdDesc: 'Standard deviation of successive differences',
    ppgLfDesc: 'Low frequency (sympathetic activity)',
    ppgHfDesc: 'High frequency (parasympathetic activity)',
    ppgLfhfDesc: 'Sympathetic/parasympathetic balance ratio',
    ppgAvnnDesc: 'Average NN interval (heart cycle)',
    ppgPnn50IdxDesc: 'RR intervals differing > 50ms ratio',
    ppgPnn20IdxDesc: 'RR intervals differing > 20ms ratio',
    // PPG Status Messages
    ppgBpmLowMsg: 'Low heart rate',
    ppgBpmHighMsg: 'High heart rate',
    ppgBpmNormalMsg: 'Normal range',
    ppgSpo2UnmeasurableMsg: 'Unmeasurable (insufficient signal)',
    ppgSpo2SevereMsg: 'Low oxygen saturation (measured value)',
    ppgSpo2MildMsg: 'Slightly low oxygen saturation',
    ppgSpo2LowNormalMsg: 'Normal range (lower)',
    ppgSpo2NormalMsg: 'Normal oxygen saturation',
    ppgRmssdLowMsg: 'Tension state (rest needed)',
    ppgRmssdHighMsg: 'High beat-to-beat variation',
    ppgRmssdNormalMsg: 'Normal range',
    ppgSdnnLowMsg: 'Regular rhythm (stress or fatigue)',
    ppgSdnnHighMsg: 'Varied rhythm',
    ppgSdnnNormalMsg: 'Normal range',
    ppgPnn50LowMsg: 'Regular rhythm (tension or fatigue)',
    ppgPnn50HighMsg: 'Flexible rhythm',
    ppgPnn50NormalMsg: 'Normal range',
    ppgStressVeryLowMsg: 'Very low stress (excessive relaxation)',
    ppgStressLowMsg: 'Low stress (relaxed state)',
    ppgStressNormalMsg: 'Normal range (balanced state)',
    ppgStressHighMsg: 'High stress (tension state)',
    ppgStressVeryHighMsg: 'Very high stress value',
    ppgAvnnHighMsg: 'Fast heartbeat (active or tense)',
    ppgAvnnLowMsg: 'Slow heartbeat (resting or athletic)',
    ppgAvnnNormalMsg: 'Stable heart rhythm',
    ppgLfLowMsg: 'Low sympathetic activity (excessive rest)',
    ppgLfHighMsg: 'High sympathetic activity (stress or tension)',
    ppgLfNormalMsg: 'Appropriate sympathetic activity',
    ppgHfLowMsg: 'Low parasympathetic activity (stress or fatigue)',
    ppgHfHighMsg: 'High parasympathetic activity (deep rest)',
    ppgHfNormalMsg: 'Appropriate parasympathetic activity',
    ppgLfhfLowMsg: 'Parasympathetic dominant (deep rest)',
    ppgLfhfHighMsg: 'Sympathetic dominant (stress or activity)',
    ppgLfhfNormalMsg: 'Autonomic balance state',
    ppgPnn20LowMsg: 'Regular rhythm (tension or fatigue)',
    ppgPnn20HighMsg: 'Flexible rhythm',
    ppgPnn20NormalMsg: 'Appropriate HR variability',
    ppgSdsdLowMsg: 'Low HR variation (stress or fatigue)',
    ppgSdsdHighMsg: 'Active HR variation',
    ppgSdsdNormalMsg: 'Normal HR variation',
    ppgHrMaxLowMsg: 'Low max heart rate',
    ppgHrMaxHighMsg: 'High max heart rate',
    ppgHrMaxNormalMsg: 'Normal max heart rate',
    ppgHrMinLowMsg: 'Low min heart rate',
    ppgHrMinHighMsg: 'High min heart rate',
    ppgHrMinNormalMsg: 'Normal min heart rate',
    // -- ACC Indexes --
    accStabilityLabel: 'Stability',
    accIntensityLabel: 'Intensity',
    accAvgMovementLabel: 'Average Movement',
    accMaxMovementLabel: 'Max Movement',
    accStabilityDesc: 'Posture stability index',
    accIntensityDesc: 'Movement intensity level',
    accAvgMovementDesc: 'Average movement magnitude',
    accMaxMovementDesc: 'Max movement magnitude',
    accAnalyzing: 'Analyzing status...',
    accSampleAnalysis: 'samples analyzed',
    accDeviceRequired: 'Device connection required',
    accActivityTitle: 'Current Activity Status',
    accTotalSamples: 'Total samples',
    accRecentActivity: 'Recent activity',
    accTotalActivity: 'Total activity',
    accIndexRange: 'Index range',
    accAccuracyDegrade: 'ACC analysis accuracy may degrade',
    // ACC Status Messages
    accStabilityVeryLowMsg: 'Very unstable posture',
    accStabilityLowMsg: 'Unstable posture',
    accStabilityNormalMsg: 'Normal stability',
    accStabilityHighMsg: 'Very stable posture',
    accIntensityLowMsg: 'Low intensity (rest, sleep)',
    accIntensityNormalMsg: 'Medium-low intensity (daily life)',
    accIntensityHighMsg: 'Medium-high intensity (exercise, work)',
    accIntensityVeryHighMsg: 'High intensity (intense exercise)',
    accAvgMoveLowMsg: 'Stationary',
    accAvgMoveNormalMsg: 'Sitting',
    accAvgMoveHighMsg: 'Walking',
    accAvgMoveVeryHighMsg: 'Running',
    accMaxMoveLowMsg: 'Stationary',
    accMaxMoveNormalMsg: 'Sitting',
    accMaxMoveHighMsg: 'Walking',
    accMaxMoveVeryHighMsg: 'Running',
    // -- Shared Index UI --
    indexNoData: 'No data',
    indexSampleAvg: 'samples avg',
    indexDeviceRequired: '(Device connection required)',
    indexDetailPreparing: 'Detailed information is being prepared.',
    indexDisclaimer: 'This app is not a medical device. All measurements are for wellness and research reference only.',
    indexStabilized: 'Stabilized',
    indexSampleCount: 'samples',
    // Tooltip
    tooltipFormula: 'Formula',
    tooltipNormalRange: 'Normal range',
    tooltipInterpretation: 'Interpretation',
    tooltipReference: 'References',
    tooltipDescription: 'Description',
    tooltipMeasurement: 'Measurement method',
    tooltipClassification: 'Classification criteria',
    tooltipUnit: 'Unit',
    // -- 차트 공통 --
    powerSpectrumConnect: 'Connect device to view power spectrum in real-time',
    powerSpectrumAnalyzing: 'Analyzing frequency spectrum from EEG data...',
    powerSpectrumFooter: 'Real-time power spectrum analysis from EEG data • Morlet Wavelet Transform',
    bandPowerConnect: 'Connect device to view EEG frequency band power in real-time',
    bandPowerAccuracyDegrade: 'Frequency analysis accuracy may degrade',
    bandPowerDiff: 'Diff',
    bandPowerLabel: 'Real-time frequency band analysis based on Power Spectrum',
    dataQualityLabel: 'Data Quality',
    spectrumLabel: 'Spectrum',
    leftBrainDominant: 'Left brain dominant',
    rightBrainDominant: 'Right brain dominant',
    balancedState: 'Balanced',
    tooltipTotalPower: 'Total Power',
    tooltipChannelPower: 'Power by Channel',
    tooltipBrainDiff: 'Hemispheric Difference',
    tooltipBandInterpretation: 'Average value of the {range} range in the Power Spectrum graph.',
    systemInfo: 'System Info',
    connectionStateLabel: 'Connection State',
    ch1SQIDataLabel: 'Ch1 SQI Data',
    ch2SQIDataLabel: 'Ch2 SQI Data',
    eegSQIComputing: 'Computing SQI from EEG data...',
    graphUpdateInterval: 'Graph Update Interval',
    measuring: 'Measuring...',
    // ACC Chart Titles
    accMagnitudeTitle: 'ACC Acceleration (gravity removed)',
    accMagnitudeNoData: 'ACC Acceleration - No Data',
    accTooltipSample: 'Sample',
    accTooltipMagnitude: 'Magnitude',
    accTooltipActivityState: 'Activity State',
    accTooltipGravityRemoved: 'Gravity removed',
    // PPG Chart Titles
    ppgFilteredTitle: 'PPG Bandpass Filtered Signal (0.5-5.0Hz, Red & IR)',
    ppgNoDataTitle: 'PPG Signal - No Data',
    ppgFilteredTooltip: 'Filtered: 0.5-5.0Hz bandpass',
    ppgSQITitle: 'PPG Signal Quality Index (SQI)',
    tooltipSample: 'Sample',
    normalRange: 'Normal range',
  },
  dataCenter: {
    title: 'Data Center',
    subtitle: 'Data storage & management',
    sessions: 'Sessions',
    totalData: 'Total Data',
    storageType: 'Storage Type',
    startRecording: 'Start Recording',
    stopRecording: 'Stop Recording',
    recording: 'Recording',
    noSessions: 'No saved sessions',
    noSessionsDesc: 'Sessions will appear here once you start recording.',
    sessionName: 'Session Name',
    duration: 'Duration',
    size: 'Size',
    date: 'Date',
    delete: 'Delete',
    download: 'Download',
    upload: 'Upload',
    uploadToCloud: 'Upload to Cloud',
    cloudStorage: 'Cloud Storage',
    localStorage: 'Local Storage',
    incompleteSessions: 'Incomplete Sessions',
    cleanup: 'Clean Up',
    storageLocation: 'Storage Location',
    storageSubtitle: 'Data storage & management',
    storageSettingsBtn: 'Storage Settings',
    selectStorageDirectory: 'Please select a storage directory',
    selectStorageDirectoryDesc: 'You need to select a local storage directory to save data.',
    selectAction: 'Select',
    storageConnected: 'Storage directory connected',
    totalStorage: 'Total Storage',
    used: 'Used',
    available: 'Available',
    realtimeStreamingRecording: 'Real-time Streaming Recording',
    deviceInfo: 'Device Info',
    connectedLinkBand: 'Connected LINK BAND Device',
    deviceNameLabel: 'Device Name',
    deviceIdLabel: 'Device ID',
    connectionStatus: 'Connection Status',
    connected: 'Connected',
    batteryLabel: 'Battery',
    connectionTime: 'Connection Time',
    samplingRateLabel: 'Sampling Rate',
    storageInfoCard: 'Storage Info',
    storageLocationLabel: 'Data Storage Location',
    storagePath: 'Storage Path',
    copyPath: 'Copy Path',
    openFolder: 'Open Folder',
    connectDeviceTitle: 'Please connect a device!',
    connectDeviceDesc: 'You need to connect a LINK BAND device to collect and record data.',
    connectDeviceTip: 'Connect a device in the Device Manager tab and try again.',
    recordingControl: 'Recording Control',
    deviceConnected: 'Device connected',
    deviceNotConnected: 'Device not connected',
    storageReady: 'Storage ready',
    storageSetupNeeded: 'Storage setup needed',
    dataStreaming: 'Data streaming',
    streamingStopped: 'Streaming stopped',
    fileSaving: 'Saving file',
    saveWaiting: 'Waiting to save',
    sessionNamePlaceholder: 'Session name (optional)',
    durationLabel: 'Duration',
    dataSize: 'Data size',
    savingProgress: 'Saving...',
    stopRecordingShort: 'Stop Recording',
    startRecordingShort: 'Start Recording',
    saveSettings: 'Save Settings',
    indexedDbInfo: '💾 IndexedDB (browser built-in storage)',
    indexedDbDesc: 'All data is stored in browser IndexedDB and can be exported to the format of your choice (CSV/JSON/ZIP) on download.',
    dataTypesToSave: 'Data types to save',
    cannotDeleteActive: 'Cannot delete an active session',
    cannotDeleteActiveDesc: 'Please wait until upload or recording is complete.',
    sessionDeleted: 'Session deleted.',
    androidDeleteLimited: 'On Android, full file deletion is limited. The session has been removed from the list.',
    sessionDeleteFailed: 'Failed to delete session.',
    androidAccessLimited: 'File system access is restricted on Android.',
    cannotDownloadActive: 'Cannot download an active session',
    cannotDownloadActiveDesc: 'Please wait until upload or recording is complete.',
    sessionDownloadFailed: 'Failed to download session.',
    cannotCopyPathActive: 'Cannot copy path for an active session',
    cannotCopyPathActiveDesc: 'Please wait until upload or recording is complete.',
    pathCopied: 'Path copied',
    pathCopiedDesc: 'The absolute path has been copied to the clipboard.',
    pathCopyFailed: 'Path copy failed',
    pathCopyFailedDesc: 'An error occurred while copying the path to the clipboard.',
    storagePathCopied: 'Storage path copied',
    storagePathCopiedDesc: 'The storage root path has been copied to the clipboard.',
    folderOpenFailed: 'Failed to open folder',
    folderOpenFailedDesc: 'An error occurred while copying the folder path.',
    storageFolderPathCopied: 'Storage folder path copied',
    stopRecordingFailedAlert: 'Failed to stop recording.',
    connectDeviceFirstAlert: 'Please connect a device first.',
    selectStorageFirstAlert: 'Please select a storage directory for local saving first.',
    awsCredentialsAlert: 'Please configure AWS credentials for S3 cloud storage.',
    startRecordingFailedAlert: 'Failed to start recording.',
    storageRestoreError: 'Storage Restore Error',
    storageRestoreErrorDesc: 'An error occurred while restoring storage.',
    allData: 'All Data',
    eegOnly: 'EEG Only',
    ppgOnly: 'PPG Only',
    accOnly: 'ACC Only',
    today: 'Today',
    thisWeek: 'This Week',
    searchPlaceholder: 'Search by session name or device...',
    advancedFilter: 'Advanced Filter',
    savedSessions: 'Saved Sessions',
    newest: 'Newest',
    oldest: 'Oldest',
    colSessionName: 'Session Name',
    colDateTime: 'Date/Time',
    colDuration: 'Duration',
    colDevice: 'Device',
    colDataTypes: 'Data Types',
    colSize: 'Size',
    colLocation: 'Storage Location',
    colActions: 'Actions',
    uploading: 'Uploading...',
    recordingInProgress: 'Recording...',
    copyAbsolutePath: 'Copy absolute path (to clipboard)',
    downloadSession: 'Download session',
    deleteSession: 'Delete session',
    noSessionsFound: 'No saved sessions',
    noSessionsFoundDesc: 'Try different search terms or filters.',
    loadingSessions: 'Loading saved sessions...',
    qualityExcellent: 'Excellent',
    qualityGood: 'Good',
    qualityFair: 'Fair',
    qualityPoor: 'Poor',
    folderWinTitle: 'Open storage folder in Windows Explorer',
    folderWinDesc: 'Press Ctrl+L in Windows Explorer and navigate to the copied path.',
    folderMacTitle: 'Open storage folder in Finder',
    folderMacDesc: 'Press ⌘+Shift+G in Finder and navigate to the copied path.',
    folderLinuxTitle: 'Open storage folder in file manager',
    folderLinuxDesc: 'Press Ctrl+L in your file manager and navigate to the copied path.',
    folderGenericTitle: 'Open storage folder in file manager',
    folderGenericDesc: 'Navigate to the copied path in your file manager.',
  },
  streamProcess: {
    title: 'Stream Process',
    subtitle: 'Real-time Broadcasting Monitoring',
    broadcastServer: 'Broadcast Server',
    serverUrl: 'Server URL',
    connect: 'Connect',
    disconnect: 'Disconnect',
    connected: 'Connected',
    notConnected: 'Not Connected',
    healthCheck: 'Health Check',
    deviceStatus: 'Device Status',
    deviceName: 'Device Name',
    deviceId: 'Device ID',
    connectionStatus: 'Connection Status',
    battery: 'Battery',
    noDeviceAlert: 'Device not connected - Test available in Mock mode',
    mode: 'Mode',
    mockMode: 'Mock Mode',
    broadcastId: 'Broadcast ID',
    mockHint: '💡 You can test broadcasting with Mock data even without a device.',
    streamingControl: 'Streaming Control',
    sent: 'Sent',
    received: 'Received',
    subscribers: 'Subscribers',
    avgResponse: 'Avg Response',
    startSubscription: 'Start Subscription',
    stopSubscription: 'Stop Subscription',
    viewStats: 'View Stats',
    mockSimulation: 'Mock Simulation',
    mockSimulationDesc: 'Send test data to test broadcasting',
    eegRawLabel: 'EEG Raw (250 samples)',
    ppgRawLabel: 'PPG Raw (50 samples)',
    accRawLabel: 'ACC Raw (30 samples)',
    analysisMetricsLabel: 'Analysis Metrics',
    combinedDataLabel: 'Combined Data (All)',
    continuousStreamLabel: 'Continuous Stream (10s)',
    stopSimulationLabel: 'Stop Simulation',
    serverStats: 'Server Stats',
    messagesLabel: 'Received Messages',
    messages: 'Messages',
    seconds: 's',
    noMessages: 'No messages received',
    noMessagesDesc: 'Start a subscription or run a simulation',
    clear: 'Clear',
    close: 'Close',
    developerApi: 'Developer API - External Service Integration Guide',
    developerApiDesc: 'Learn how to subscribe to real-time sensor data from external services or applications. Simple integration using SSE (Server-Sent Events).',
    sseSubscribe: 'Subscribe via SSE',
    copy: 'Copy',
    messageStructure: 'Message Data Structure',
    optionalFieldsNote: '💡 All fields are optional. The sender only includes the necessary data.',
    apiEndpoints: 'API Endpoints',
    subscribeEndpointDesc: 'Receive real-time data via SSE stream',
    publishEndpointDesc: 'Publish data (automatically sent from browser)',
    healthEndpointDesc: 'Check server status',
    statsEndpointDesc: 'View server statistics',
    quickTest: 'Quick Test',
    quickTestTerminal: 'Test directly from terminal:',
    quickTestDesc: 'After running the command above in your terminal, send Mock data from this page to see real-time data in your terminal.',
    pythonSSEClient: 'Python SSE Client',
    curlTest: 'Test with cURL',
    serverHealthCheck: 'Server Health Check',
    serverStatsQuery: 'Server Statistics',
    curlBufferNote: 'The -N option disables buffering to receive data in real-time.',
    status: 'Status',
    uptime: 'Uptime',
    activeDevice: 'Active Devices',
    totalConnections: 'Total Connections',
    libRequired: 'Required package:',
    connectedToast: 'Connected to broadcast server',
    connectedMockDesc: 'Connected in Mock mode',
    connectFailed: 'Server connection failed',
    unknownError: 'Unknown error',
    disconnectedToast: 'Broadcast server disconnected',
    healthOk: 'Health check complete',
    healthFail: 'Health check failed',
    statsOk: 'Server stats retrieved',
    statsFail: 'Stats retrieval failed',
    connectFirst: 'Please connect to the server first',
    dataSent: 'Data sent',
    dataSentDesc: 'Sent to {count} subscribers ({time}ms)',
    dataSendFail: 'Data transmission failed',
    simulationComplete: 'Simulation complete (10s)',
    simulationStarted: 'Continuous stream simulation started (10s)',
    simulationStopped: 'Simulation manually stopped',
    subscriptionStarted: 'Subscription started',
    subscriptionStopped: 'Subscription stopped',
    copyJSToast: 'JavaScript code copied',
    copyPythonToast: 'Python code copied',
    copyCurlToast: 'cURL command copied',
    copyCmdToast: 'Command copied',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage application settings',
    deviceManager: 'Device Manager',
    deviceManagerDesc: 'Device connection and management settings',
    autoConnect: 'Auto Connect',
    autoConnectDesc: 'Automatically connect to scanned devices.',
    autoRegister: 'Auto Registration',
    autoRegisterDesc: 'Automatically register devices upon connection for faster reconnection.',
    deviceManagerInfo: '💡 Device Management Guide:',
    autoConnectInfo: 'Auto Connect: Automatically connects to scanned devices',
    autoRegisterInfo: 'Auto Register: Automatically registers connected devices for faster next connection',
    manualModeInfo: 'Disabling both features enables fully manual mode',
    noDuplicateInfo: 'Already registered devices will not be duplicated',
    timeZone: 'Time Zone',
    timeZoneDesc: 'Time display settings',
    timeZoneLabel: 'Time Zone',
    timeZoneSystem: 'System Timezone',
    timeZoneKorea: 'Korea Time (KST/GMT+9)',
    timeZoneUtc: 'Universal Standard Time (UTC)',
    timeZoneNote: 'Applies to session start/end times and all time displays',
    timeZoneInfo: '💡 Time Zone Settings Guide:',
    timeZoneSystemInfo: 'System Timezone: Uses your current computer timezone',
    timeZoneKoreaInfo: 'Korea Time: Always displays in KST (UTC+9)',
    timeZoneUtcInfo: 'UTC: Displays in Universal Standard Time',
    timeZoneApplyInfo: 'Changes take effect from the next new session',
    visualizer: 'Visualizer',
    visualizerDesc: 'Data visualization settings',
    visualizerComingSoon: 'Visualization settings are under development.',
    language: 'Language',
    languageDesc: 'Interface language settings',
    languageKo: '한국어',
    languageEn: 'English',
  },
  documents: {
    title: 'Documents',
    subtitle: 'View and browse SDK development related documentation.',
    searchPlaceholder: 'Search documents...',
    browseDocs: 'Browse Documentation',
    browseDocsDesc: 'Find what you need by topic.',
    viewDocs: 'View docs',
    contents: 'Contents',
    docs: 'Docs',
    previous: 'Previous',
    next: 'Next',
    selectDoc: 'Select a document',
    selectDocDesc: 'Choose a document from the sidebar to view its content.',
    sdkDocumentation: 'SDK Documentation',
    sdkHeroDesc: 'Developer guide for connecting to LINK BAND 2.0 via Web Bluetooth and collecting EEG, PPG, ACC biosignals in real time.',
    menuLabel: 'Open menu',
    sectionDesc: {
      overview: 'Architecture and core concepts',
      gettingStarted: 'From install to first connection',
      bleProtocol: 'Service UUIDs, packet structure, timestamps',
      apiReference: 'API reference for all SDK modules',
      dataGuide: 'Data formats and export guide',
      interface: 'Interface guide for all pages',
    },
  },
  exportModal: {
    title: 'Export Session',
    fileSize: 'File size',
    formatSelection: 'Select export format',
    recommended: 'Recommended',
    cancel: 'Cancel',
    selectLocation: 'Select save location',
    saveMethodTitle: 'Choose save method',
    saveToDevice: 'Save to device',
    saveToDeviceDesc: 'Save as a file to device storage (Files app)',
    shareToApps: 'Share to apps',
    shareToAppsDesc: 'Share via OS share sheet',
    saveLocalSuccessDesc: 'File saved to device storage.',
    locationSelecting: 'Selecting save location...',
    locationSelectingDesc: 'Please select a folder in the browser to save the files.',
    progressTitle: 'Exporting...',
    progressZip: 'Creating folder and copying files.',
    progressAll: 'Exporting all files individually.',
    progressFormat: (format: string) => `Exporting ${format} file.`,
    progressLabel: 'Progress',
    successTitle: 'Export complete!',
    successDesc: 'Session data exported successfully.',
    done: 'Done',
    errorTitle: 'Export failed',
    retry: 'Retry',
    unknownError: 'An unknown error occurred.',
    fmtZipName: 'Full session (folder)',
    fmtZipDesc: 'Export all files and metadata as a folder',
    fmtCsvName: 'CSV files only',
    fmtCsvDesc: 'CSV format readable in spreadsheets',
    fmtJsonName: 'JSON files only',
    fmtJsonDesc: 'Structured JSON format',
    fmtJsonlName: 'JSON Lines files only',
    fmtJsonlDesc: 'JSONL format optimized for streaming',
    fmtBinaryName: 'Binary files only',
    fmtBinaryDesc: 'Most compressed binary format',
    fmtAllName: 'All files (individual)',
    fmtAllDesc: 'Export all formats as individual files',
    sizeCompressed50: '~50% compressed',
    sizeOriginal70: '~70% original',
    sizeOriginal65: '~65% original',
    sizeCompressed30: '~30% compressed',
  },
  sessionManager: {
    recordingControl: '📊 Session Recording Control',
    sessionNameOptional: 'Session name (optional)',
    startRecording: 'Start session recording',
    stopRecording: 'Stop session recording',
    recordingPrefix: '🔴 Recording',
    duration: 'Duration',
    dataLabel: 'Data',
    liveBadge: 'LIVE',
    connectDeviceFirst: 'Please connect a device first',
    deviceNotConnectedAlert: 'Device is not connected.',
    currentSession: 'Current Session',
    inProgress: 'In progress',
    sessionName: 'Session name',
    startTime: 'Start time',
    device: 'Device',
    dataCollection: 'Data collection',
    exportAllZip: 'Download all data as ZIP',
    exportEeg: 'Export EEG',
    exportPpg: 'Export PPG',
    exportAcc: 'Export ACC',
    exportSystemLog: 'Export system log',
    storageInfo: 'Storage Info',
    storedSessions: 'Stored sessions',
    used: 'Used',
    available: 'Available',
    usageRate: 'Storage usage',
    refresh: 'Refresh',
    noSavedSessions: 'No saved sessions',
    savedSessionsCount: (count: number) => `Saved sessions (${count})`,
    colSession: 'Session',
    colDevice: 'Device',
    colType: 'Type',
    colDate: 'Date',
    colDuration: 'Duration',
    colSamples: 'Samples',
    colSize: 'Size',
    colStatus: 'Status',
    colActions: 'Actions',
    statusCompleted: 'Completed',
    statusProcessing: 'Processing',
    exportDialogTitle: 'Export Data',
    exportDialogSessionLabel: 'Session',
    exportDialogSelect: 'Select the data type to export',
    zipDescription: 'Download all data and session info as a single ZIP file',
    eegDataCount: (n: number) => `EEG data (${n.toLocaleString()})`,
    ppgDataCount: (n: number) => `PPG data (${n.toLocaleString()})`,
    accDataCount: (n: number) => `Accelerometer data (${n.toLocaleString()})`,
    systemLogLabel: 'System log',
    previous: 'Previous',
    next: 'Next',
    sessionRangeOf: (start: number, end: number, total: number) => `${start}-${end} of ${total} sessions`,
    confirmDelete: 'Delete this session? This action cannot be undone.',
    csvNotFoundAlert: 'CSV file not found.',
    exportFailedAlert: 'Session export failed: csv file not found.',
    exportButton: 'Export',
  },
  brainStateIndicator: {
    title: 'Brain State Indicator',
    deviceRequired: 'Device connection required',
    sensorContactRequired: 'Sensor contact check required',
    instruction: 'Please connect the LINK BAND device and ensure proper sensor contact',
    movingAverage: 'Stabilized via Moving Average',
    footerNote: 'Based on real-time EEG data • Stabilized over 15-sample Moving Average',
    focus: 'Focus',
    relaxation: 'Relaxation',
    stress: 'Stress',
    attention: 'Attention',
    meditation: 'Meditation',
    cognitiveLoad: 'Cognitive Load',
    levelLow: 'Low',
    levelMedium: 'Medium',
    levelHigh: 'High',
  },
  performanceMonitor: {
    title: 'Performance Monitor',
    notConnected: 'Not connected',
    statusGood: 'Good',
    statusWarning: 'Warning',
    statusCritical: 'Critical',
    statusUnknown: 'Unknown',
    processingTime: 'Processing time',
    memoryUsage: 'Memory usage',
    bufferSize: 'Buffer size',
    droppedPackets: 'Dropped packets',
    timestampSync: 'Timestamp sync',
    timestampDrift: 'Timestamp drift',
    packetsUnit: '',
  },
  browserCompatibility: {
    title: 'Browser Compatibility Status',
    currentBrowser: (browser: string, platform: string) => `Current browser: ${browser} on ${platform}`,
    bluetoothAPILabel: 'Web Bluetooth API',
    supported: 'Supported',
    unsupported: 'Unsupported',
    secure: 'Secure',
    insecure: 'Insecure',
    securityContext: 'Secure Context (HTTPS)',
    unsupportedAlert: 'Your current browser does not support LINK BAND device connection.',
    iOSUserGuide: 'iOS User Guide',
    iOSSafariDesc: 'iOS Safari doesn\'t support Web Bluetooth, but there are alternatives!',
    capacitorNativeTitle: '✅ LINK BAND SDK Native App',
    capacitorNativeDesc: 'Native BLE plugin connects directly to LINK BAND devices.',
    bluefyTitle: '✅ Recommended: Bluefy Browser',
    bluefyDesc: 'A dedicated iOS browser with Web Bluetooth support.',
    downloadBluefy: 'Download Bluefy from App Store',
    webBLETitle: '🔧 Alternative: WebBLE Browser',
    webBLEDesc: 'Another Web Bluetooth-capable browser for iOS.',
    downloadWebBLE: 'Download WebBLE Browser',
    howToUse: '⚡ How to Use',
    step1: '1. Download one of the apps above',
    step2: '2. Open sdk.linkband.store in the app',
    step3: '3. Connect and use your LINK BAND device',
    androidUserGuide: 'Android User Guide',
    androidSupportedMsg: 'LINK BAND connection is available on your current browser!',
    androidChromeMsg: 'Please use Chrome or Edge browser.',
    androidReadyTitle: '✅ Ready to Connect',
    androidReadyDesc: 'Go to the LINK BAND page and connect your device.',
    androidBrowserTitle: '🔧 Recommended Browser',
    androidBrowserDesc: 'Please open this site in Chrome or Edge browser.',
    downloadChrome: 'Download Chrome Browser',
    desktopUserGuide: 'Desktop User Guide',
    desktopSupportedMsg: 'LINK BAND connection is available on your current browser!',
    desktopBrowserMsg: 'Please use Chrome, Edge, or Opera browser.',
    desktopReadyTitle: '✅ Ready to Connect',
    desktopReadyDesc: 'Go to the LINK BAND page and connect your device.',
    desktopBrowserTitle: '🔧 Supported Browsers',
    supportedBrowsers: 'Web Bluetooth is supported in the following browsers:',
    showDetails: 'Show Details',
    hideDetails: 'Hide Details',
    technicalDetails: 'Technical Details',
  },
  mobileStorageGuide: {
    title: 'Android Data Storage Guide',
    description: 'Information about data storage on Android devices.',
    autoDownloadTitle: 'Auto Download Method',
    autoDownloadDesc: 'On Android, data is automatically saved to the Downloads folder.',
    currentSession: 'Current Session',
    eegData: (n: number) => `EEG data: ${n}`,
    ppgData: (n: number) => `PPG data: ${n}`,
    accData: (n: number) => `ACC data: ${n}`,
    howItWorks: 'How It Works',
    autoSave30s: 'CSV files automatically created every 30 seconds during recording',
    autoSave50MB: 'Auto-saves every 50MB to conserve memory',
    finalSave: 'Final data saved when recording ends',
    checkDownloads: 'Files accessible in Downloads folder',
    savedFiles: 'Saved Files',
    openDownloads: 'Open Downloads Folder',
    confirm: 'OK',
    note: 'Note',
    noteTimestamp: 'File names include timestamps for easy identification.',
    noteTransfer: 'You can transfer files to your computer for analysis or backup.',
    manualCheck: 'Please check your Downloads folder manually.\n\nFile Manager → Downloads folder → find your LINK BAND data.',
  },
};

const translations = {
  ko,
  en,
};

export const useTranslation = (language: Language): TranslationKeys => {
  return translations[language];
};
